import { Injectable, signal, computed, effect, EventEmitter } from '@angular/core';
import { BehaviorSubject, Subject, Observable, firstValueFrom } from 'rxjs';
import { filter, take, timeout, catchError, takeUntil } from 'rxjs/operators';
import { WebSocketSignalingService } from './websocket-signaling.service';
import { DeviceService } from './device.service';
import { AttendanceService, TicketRecord } from './attendance.service';
import { EventService } from './event.service';
import { AudioService } from './audio.service'; // NEW: Import AudioService
import {
    WebRTCPeer,
    ChannelMessage,
    PeerConnectionEvent,
    ChannelType,
    ChannelConfig,
    DEFAULT_WEBRTC_CONFIG,
    WebRTCConfiguration,
    SignalingMessage
} from '../models/webrtc.models';
import { DebugLoggerService } from './debug-logger.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertController, ToastController } from '@ionic/angular/standalone';
import { StorageService } from './storage.service';
import { ServerSyncService } from './server-sync.service';
import { TurnCredentialsService } from './turn-credentials.service';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

@Injectable({
    providedIn: 'root'
})
export class WebrtcMeshService {
    private peers = new Map<string, WebRTCPeer>();
    private localStream?: MediaStream;
    private config: WebRTCConfiguration = DEFAULT_WEBRTC_CONFIG;
    private destroy$ = new Subject<void>();

    private _isCreator = false; // Added to store creator status

    // Connection history for device recognition
    private readonly CONNECTION_HISTORY_KEY = 'webrtc_connection_history';
    private readonly MAX_HISTORY_ENTRIES = 10;

    // Broadcast intent persistence for auto-restore after reconnect
    private readonly BROADCAST_INTENT_KEY = 'webrtc_broadcast_intent';

    // Health monitoring for orphaned device detection (cellular-friendly timeouts)
    private readonly PEER_HEALTH_CHECK_INTERVAL = 15000; // 15 seconds
    private readonly PEER_TIMEOUT = 180000; // 180 seconds without activity
    private readonly HEARTBEAT_INTERVAL = 10000; // 10 seconds
    private readonly DATA_CHANNEL_OPEN_TIMEOUT_MS = 20000; // 20 seconds
    private readonly CONNECTING_STALE_MS = 60000; // 60 seconds
    private readonly DATA_CHANNEL_BUFFERED_AMOUNT_LIMIT = 65536; // 64KB backpressure
    private readonly LEAN_MODE_RTT_MS = 800;
    private healthCheckTimer?: any;
    private heartbeatTimer?: any;
    private lastSeenMap = new Map<string, number>();
    private peerRttMap = new Map<string, number>();
    private syncRequestHandlers = new Map<string, (response: any) => void>();
    private unhandledSignalingTypeCounts = new Map<string, number>();

    // NEW: Initialization lock to prevent concurrent initMesh calls
    private initializationInProgress = false;
    private currentEventId: string | null = null;

    // Debounce timer for count_sync relay to avoid flooding peers during rapid increments
    private _countSyncRelayTimer: any = null;
    private _countSyncRelayEventId: string | null = null;

    // Event emitter for reconnection audit requests
    private _reconnectionAuditRequested = new Subject<{
        eventId: string;
        localCount: number;
        peerConsensus: number;
        details: any;
    }>();

    public readonly reconnectionAuditRequested$ = this._reconnectionAuditRequested.asObservable();

    // WalkyTalky voice message delivery
    private _voiceMessageReceived = new Subject<{
        eventId: string;
        messageId: string;
        mimeType: string;
        dataUrl: string;
        createdAt: number;
        sender: {
            deviceId?: string;
            deviceName?: string;
            displayName?: string;
            platform?: string;
        };
    }>();
    public readonly voiceMessageReceived$ = this._voiceMessageReceived.asObservable();

    private voiceMessageChunks = new Map<string, {
        totalChunks: number;
        chunks: string[];
        eventId: string;
        mimeType: string;
        createdAt: number;
        sender: {
            deviceId?: string;
            deviceName?: string;
            displayName?: string;
            platform?: string;
        };
    }>();
    private readonly WALKY_TALKY_ENABLED_KEY = 'walky_talky_enabled';
    private readonly WALKY_TALKY_TARGET_KEY = 'walky_talky_target';
    private readonly WALKY_TALKY_POLICY_TS_KEY = 'walky_talky_policy_ts';
    private readonly WALKY_TALKY_SETTINGS_EVENT = 'walky-talky-settings-changed';

    // NEW: Primary signal-based state
    private readonly _meshStatusSignal = signal<boolean>(false);
    private readonly _connectedPeersSignal = signal<WebRTCPeer[]>([]);
    private readonly _eventsSignal = signal<PeerConnectionEvent | null>(null);
    private readonly _dataMessagesSignal = signal<ChannelMessage | null>(null);
    private readonly _cachedMessagesSignal = signal<Map<string, ChannelMessage[]>>(new Map());
    private readonly _reconnectionStatusSignal = signal<'idle' | 'reconnecting' | 'failed'>('idle');

    // Background mode state tracking
    private isInBackground = false;
    private lastForegroundSync = Date.now();
    private readonly DECREASE_ALLOW_WINDOW_MS = 2 * 60 * 1000;
    private allowDecreaseUntilByEvent = new Map<string, number>();

    // Reconnection state
    private reconnectionAttempts = 0;
    private readonly MAX_RECONNECTION_ATTEMPTS = 3;
    private reconnectionTimer?: any;
    private readonly BLOCKED_RECONNECT_KEY = 'blocked_reconnect_events';
    private readonly ownerConflictAlertSeenAt = new Map<number, number>();

    // NEW: Public readonly signals
    public readonly meshStatusSignal = this._meshStatusSignal.asReadonly();
    public readonly connectedPeersSignal = this._connectedPeersSignal.asReadonly();
    public readonly currentEventSignal = this._eventsSignal.asReadonly();
    public readonly currentDataMessageSignal = this._dataMessagesSignal.asReadonly();
    public readonly cachedMessagesSignal = this._cachedMessagesSignal.asReadonly();
    public readonly reconnectionStatusSignal = this._reconnectionStatusSignal.asReadonly();

    // NEW: Computed signals for derived state
    public readonly isInitializedSignal = computed(() => this.meshStatusSignal());
    public readonly peerCountSignal = computed(() => this.connectedPeersSignal().length);
    public readonly hasConnectedPeersSignal = computed(() => this.peerCountSignal() > 0);
    public readonly connectedPeerIdsSignal = computed(() =>
        this.connectedPeersSignal().map(peer => peer.id)
    );
    public readonly meshConnectionStateSignal = computed(() => {
        const isInitialized = this.meshStatusSignal();
        const peerCount = this.peerCountSignal();
        return {
            initialized: isInitialized,
            connected: isInitialized && peerCount > 0,
            peerCount,
            status: isInitialized ? (peerCount > 0 ? 'connected' : 'initialized') : 'disconnected'
        };
    });

    // Cache messages by type (still needed)
    private _cachedMessages = new Map<string, ChannelMessage[]>();

    // Legacy compatibility - still needed by some components during migration
    public connectionStatus = new BehaviorSubject<Record<string, any>>({});

    // Diagnostics tools for debugging
    public diagnosticsTools = {
        getPeersDebugInfo: () => this.getPeersDebugInfo(),
        getConnectionDiagnostics: () => this.getConnectionDiagnostics(),
        logDiagnostics: (context?: string) => this.logDiagnostics(context || 'external'),
        forceConnections: () => this.forceConnections(),
        triggerHealthCheck: () => this.performHealthCheck(),
        getOrphanedPeers: () => this.getOrphanedPeers(),
        cleanupStalePeers: () => this.cleanupStalePeers()
    };

    constructor(
        private signalingService: WebSocketSignalingService,
        private deviceService: DeviceService,
        private attendanceService: AttendanceService,
        private eventService: EventService,
        private serverSyncService: ServerSyncService,
        private turnCredentialsService: TurnCredentialsService,
        private debugLoggerService: DebugLoggerService,
        private translateService: TranslateService,
        private audioService: AudioService, // NEW: Inject AudioService
        private alertController: AlertController,
        private toastController: ToastController,
        private storage: StorageService
    ) {
        this.setupSignalingHandlers();

        this.debugLoggerService.log('WebRTC Mesh', 'WebRTC Mesh Service initialized');

        // Update legacy connectionStatus when connectedPeers changes (using signals)
        effect(() => {
            const peers = this._connectedPeersSignal();
            const status: Record<string, any> = {};
            peers.forEach(peer => {
                status[peer.id] = {
                    connected: true,
                    deviceName: peer.deviceName,
                    platform: peer.platform,
                    lastSeen: this.lastSeenMap.get(peer.id) || Date.now()
                };
            });
            this.connectionStatus.next(status);
        });

        // Start health monitoring when mesh is initialized (using signals)
        effect(() => {
            const isActive = this._meshStatusSignal();
            if (isActive) {
                this.startHealthMonitoring();
            } else {
                this.stopHealthMonitoring();
            }
        });
    }

    private logUnhandledSignalingType(message: SignalingMessage): void {
        const type = String(message?.type || 'unknown');
        const nextCount = (this.unhandledSignalingTypeCounts.get(type) || 0) + 1;
        this.unhandledSignalingTypeCounts.set(type, nextCount);

        // Log only first few and periodic repeats to reduce telemetry noise.
        if (nextCount === 1 || nextCount === 5 || nextCount % 25 === 0) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Unhandled signaling message type observed', {
                type,
                count: nextCount,
                eventId: message?.eventId || null,
                senderId: message?.senderId || null
            });
        }
    }

    private getPeersDebugInfo(): any {
        return Array.from(this.peers.values()).map(peer => ({
            id: peer.id,
            deviceName: peer.deviceName,
            platform: peer.platform,
            connectionState: peer.connection.connectionState,
            iceConnectionState: peer.connection.iceConnectionState
        }));
    }

    private getConnectionDiagnostics(): any {
        return {
            totalPeers: this.peers.size,
            connectedPeers: this.getConnectedPeers().length,
            signalingConnected: this.signalingService.isConnected,
            clientId: this.signalingService.clientId,
            meshInitialized: this.isInitialized()
        };
    }

    async initializeMesh(config?: Partial<WebRTCConfiguration>, enableMedia?: { audio?: boolean; video?: boolean }): Promise<void> {
        if (config) {
            this.config = { ...this.config, ...config };
        }

        this.debugLoggerService.log('WebRTC Mesh', 'initializeMesh called', { config, enableMedia });

        try {
            await this.refreshIceServersFromTurn();

            // Only request media if explicitly enabled by user
            if (enableMedia && (enableMedia.audio || enableMedia.video)) {
                if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                    try {
                        this.localStream = await navigator.mediaDevices.getUserMedia({
                            video: enableMedia.video || false,
                            audio: enableMedia.audio || false
                        });
                        this.debugLoggerService.log('WebRTC Mesh', 'Local media stream acquired', {
                            hasVideo: enableMedia.video && this.localStream.getVideoTracks().length > 0,
                            hasAudio: enableMedia.audio && this.localStream.getAudioTracks().length > 0
                        });
                    } catch (mediaError) {
                        this.debugLoggerService.warn('WebRTC Mesh', `Could not access camera/microphone: ${mediaError}`);
                        this.debugLoggerService.log('WebRTC Mesh', 'Continuing without local media stream - data channels will still work');
                    }
                } else {
                    this.debugLoggerService.warn('WebRTC Mesh', 'getUserMedia not available - likely due to HTTP instead of HTTPS');
                    this.debugLoggerService.log('WebRTC Mesh', 'Media streaming disabled, but data channels will work');
                }
            } else {
                this.debugLoggerService.log('WebRTC Mesh', 'WebRTC Mesh initialized without media streams - data channels only mode');
                this.debugLoggerService.log('WebRTC Mesh', 'This prevents conflicts with QR scanner camera access');
            }

            // Connect to signaling server
            await this.signalingService.connect(this.config.signalingServerUrl);

            this._meshStatusSignal.set(true);

            this.debugLoggerService.log('WebRTC Mesh', 'WebRTC Mesh initialized successfully');
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to initialize WebRTC Mesh: ${error}`);
            throw error;
        }
    }

    // Legacy compatibility method
    async initMesh(eventId: string, isCreator: boolean = false, eventPassword?: string): Promise<string> {
        this.debugLoggerService.log('WebRTC Mesh', `Initializing mesh for event: ${eventId}, Is Creator: ${isCreator}`);

        // NEW: Prevent concurrent initialization calls
        if (this.initializationInProgress) {
            this.debugLoggerService.log('WebRTC Mesh', `Initialization already in progress for event: ${eventId}, waiting...`);
            // Wait for current initialization to complete
            while (this.initializationInProgress) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            // Return if we're already initialized for this event
            if (this.currentEventId === eventId && this.isInitialized()) {
                this.debugLoggerService.log('WebRTC Mesh', `WebRTC mesh already initialized for event: ${eventId} after waiting`);
                return eventId;
            }
        }

        // If already initialized for the same event, return
        if (this.isInitialized() && this.currentEventId === eventId && this._isCreator === isCreator) {
            this.debugLoggerService.log('WebRTC Mesh', `WebRTC mesh already initialized for event: ${eventId}`);
            return eventId;
        }

        // Set initialization lock
        this.initializationInProgress = true;

        try {
            if (this.currentEventId && this.currentEventId !== eventId) {
                this.cleanupMesh();
            }
            this.currentEventId = eventId;
            this._isCreator = isCreator; // Store the creator status

            await this.refreshIceServersFromTurn();

            // Ensure the WebSocket signaling connection is established before joining
            await this.signalingService.connect();
            await this.signalingService.joinEventRoom(eventId, eventPassword || '');

            this._meshStatusSignal.set(true);
            this.debugLoggerService.log('WebRTC Mesh', `WebRTC mesh initialized for event: ${eventId}`);

            // Store connection history for device recognition
            await this.storeConnectionHistory(eventId, eventPassword || '');

            // Add creator's own device info to the signaling service's connected devices map
            // This ensures the creator appears in the connected devices list
            if (isCreator) {
                try {
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    const clientId = this.signalingService.clientId;

                    if (clientId) {
                        // Get current attendee count for the creator
                        let attendeeCount: number | undefined;
                        try {
                            const currentEvent = this.eventService.getCurrentEvent();
                            if (currentEvent?.id) {
                                attendeeCount = await this.attendanceService.getCount(currentEvent.id);
                            }
                        } catch (error) {
                            this.debugLoggerService.warn('WebRTC Mesh', `Could not get attendee count for creator device info: ${error}`);
                        }

                        // Update the signaling service with the creator's device info
                        this.signalingService.updatePeerDeviceInfo(clientId, {
                            deviceName: deviceInfo.deviceName,
                            platform: deviceInfo.platform,
                            deviceId: deviceInfo.deviceId,
                            userDisplayName: deviceInfo.userDisplayName,
                            abbreviatedName: deviceInfo.abbreviatedName,
                            userPhoneNumber: deviceInfo.userPhoneNumber,
                            attendeeCount,
                            isCreator: true
                        });

                        this.debugLoggerService.log('WebRTC Mesh', `Added creator device info to signaling service: ${clientId}`, {
                            deviceName: deviceInfo.deviceName,
                            isCreator: true,
                            attendeeCount
                        });

                        // Update connected peers to trigger UI refresh
                        this.updateConnectedPeers();
                    }
                } catch (error) {
                    this.debugLoggerService.warn('WebRTC Mesh', `Failed to add creator device info to signaling service: ${error}`);
                }
            }

            return eventId;
        } finally {
            // Always release the lock
            this.initializationInProgress = false;
            this.debugLoggerService.log('WebRTC Mesh', 'Initialization lock released');
        }
    }

    private async refreshIceServersFromTurn(): Promise<void> {
        try {
            const iceServers = await this.turnCredentialsService.getIceServers();
            this.config = {
                ...this.config,
                iceServers
            };
            const hasTurn = iceServers.some(server => {
                const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
                return urls.some(url => typeof url === 'string' && url.startsWith('turn:'));
            });
            this.debugLoggerService.log('WebRTC Mesh', `ICE servers refreshed (TURN ${hasTurn ? 'enabled' : 'unavailable'})`, {
                serverCount: iceServers.length
            });
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', `TURN refresh failed; continuing with existing ICE servers: ${error}`);
        }
    }

    private signalingPeerClientId(peer: string | { clientId?: string } | null | undefined): string | null {
        if (typeof peer === 'string' && peer) {
            return peer;
        }
        if (peer && typeof peer === 'object' && typeof peer.clientId === 'string' && peer.clientId) {
            return peer.clientId;
        }
        return null;
    }

    async connectToPeer(rawPeerId: string | { clientId?: string } | null | undefined): Promise<void> {
        const peerId = this.signalingPeerClientId(rawPeerId);
        if (!peerId) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Ignoring connectToPeer with missing clientId', rawPeerId);
            return;
        }

        // Prevent self-connections
        if (peerId === this.signalingService.clientId) {
            this.debugLoggerService.log('WebRTC Mesh', `Ignoring self-connection attempt to ${peerId}`);
            return;
        }

        // Check for existing peer and evaluate its state
        const existingPeer = this.peers.get(peerId);
        if (existingPeer) {
            const connectionState = existingPeer.connection?.connectionState;
            const dataChannelState = existingPeer.dataChannel?.readyState;

            // If the RTP connection is alive, keep it -- media (audio/video) flows over RTP,
            // not the SCTP data channel, so a closed data channel doesn't mean audio stopped.
            if (connectionState === 'connected' && existingPeer.isConnected) {
                this.debugLoggerService.log('WebRTC Mesh', `RTP connection alive for peer ${peerId}, keeping existing connection`, {
                    connectionState,
                    dataChannelState
                });
                return;
            }

            // Clean up truly dead/stale connections before creating new one
            if (connectionState === 'failed' || connectionState === 'disconnected' ||
                connectionState === 'closed' ||
                (connectionState === 'connecting' && this.isConnectionStale(existingPeer))) {

                this.debugLoggerService.log('WebRTC Mesh', `Cleaning up stale connection to ${peerId}`, {
                    connectionState,
                    dataChannelState,
                    isConnected: existingPeer.isConnected
                });
                this.cleanupOrphanedPeer(peerId);
            } else {
                this.debugLoggerService.log('WebRTC Mesh', `Connection attempt already in progress for ${peerId}`, {
                    connectionState,
                    dataChannelState
                });
                return;
            }
        }

        const peer = await this.createPeer(peerId, true);
        await this.createOffer(peer);
    }

    private isConnectionStale(peer: WebRTCPeer): boolean {
        // Check if connection has been in connecting state for too long
        const timeSinceCreation = Date.now() - (peer.lastSeen || 0);
        return timeSinceCreation > this.CONNECTING_STALE_MS;
    }

    disconnectFromPeer(peerId: string): void {
        const peer = this.peers.get(peerId);
        if (peer) {
            peer.connection.close();
            this.peers.delete(peerId);
            this.updateConnectedPeers();

            this._eventsSignal.set({
                type: 'peer-disconnected',
                peerId
            });
            this.debugLoggerService.log('WebRTC Mesh', `Disconnected from peer: ${peerId}`);
        }
    }

    private async waitUntilDataChannelOpen(peerId: string, timeoutMs: number = this.DATA_CHANNEL_OPEN_TIMEOUT_MS): Promise<boolean> {
        const peer = this.peers.get(peerId);
        if (!peer || !peer.dataChannel) {
            this.debugLoggerService.warn('WebRTC Mesh', `Cannot wait for data channel open: Peer ${peerId} or its data channel not found.`);
            return false;
        }

        if (peer.dataChannel.readyState === 'open') {
            return true;
        }

        let attempts = 0;
        const maxAttempts = timeoutMs / 100; // Check every 100ms
        return new Promise(resolve => {
            const interval = setInterval(() => {
                if (peer.dataChannel?.readyState === 'open') {
                    clearInterval(interval);
                    resolve(true);
                } else if (attempts >= maxAttempts) {
                    clearInterval(interval);
                    this.debugLoggerService.warn('WebRTC Mesh', `Data channel for peer ${peerId} did not open within timeout.`);
                    resolve(false);
                }
                attempts++;
            }, 100);
        });
    }

    sendDataMessage(message: ChannelMessage, targetPeerId?: string): void {
        let peersToSendTo: WebRTCPeer[];

        if (targetPeerId) {
            // Sending to specific peer
            const targetPeer = this.peers.get(targetPeerId);
            peersToSendTo = targetPeer ? [targetPeer] : [];
        } else {
            // Broadcasting to all peers - filter to only healthy ones
            peersToSendTo = this.getConnectedPeers().filter(peer =>
                peer.connection?.connectionState === 'connected' &&
                peer.dataChannel?.readyState === 'open' &&
                peer.isConnected
            );
        }

        peersToSendTo.forEach(async peer => { // Make async
            if (!peer) { // Ensure peer is not undefined
                return;
            }
            if (peer.dataChannel) {
                if (peer.dataChannel.readyState !== 'open') {
                    this.debugLoggerService.log('WebRTC Mesh', `Data channel for peer ${peer.id} not open (${peer.dataChannel.readyState}). Waiting...`);
                    const opened = await this.waitUntilDataChannelOpen(peer.id);
                    if (!opened) {
                        this.debugLoggerService.warn('WebRTC Mesh', `Skipping message to ${peer.id}: Data channel did not open in time.`);
                        return; // Skip sending if channel didn't open
                    }
                }
                try {
                    const jsonMessage = JSON.stringify(message);
                    if (!this.canSendOnDataChannel(peer.dataChannel, message.type)) {
                        this.debugLoggerService.warn('WebRTC Mesh', `Skipping message to ${peer.id}: data channel backpressure`, {
                            bufferedAmount: peer.dataChannel.bufferedAmount,
                            messageType: message.type
                        });
                        return;
                    }
                    this.debugLoggerService.log('WebRTC Mesh', `Sending data message to ${peer.id}, Type: ${message.type}`);
                    peer.dataChannel.send(jsonMessage);
                } catch (error) {
                    this.debugLoggerService.error('WebRTC Mesh', `Failed to send data message to ${peer.id}: ${error}`);
                }
            } else {
                this.debugLoggerService.warn('WebRTC Mesh', `No data channel available for peer ${peer.id}.`);
            }
        });
    }

    public async sendVoiceMessageToPeer(
        targetPeerId: string,
        payload: {
            eventId: string;
            messageId: string;
            mimeType: string;
            base64Data: string;
            createdAt: number;
        }
    ): Promise<void> {
        const deviceInfo = await this.deviceService.getDeviceInfo();
        const chunkSize = 12000;
        const totalChunks = Math.ceil(payload.base64Data.length / chunkSize);

        for (let index = 0; index < totalChunks; index++) {
            const start = index * chunkSize;
            const chunk = payload.base64Data.slice(start, start + chunkSize);

            this.sendDataMessage({
                type: 'system',
                data: {
                    type: 'voice_message_chunk',
                    eventId: payload.eventId,
                    messageId: payload.messageId,
                    chunkIndex: index,
                    totalChunks,
                    chunk,
                    mimeType: payload.mimeType,
                    createdAt: payload.createdAt,
                    sender: {
                        deviceId: deviceInfo.deviceId,
                        deviceName: deviceInfo.deviceName,
                        displayName: deviceInfo.userDisplayName || deviceInfo.userName || deviceInfo.deviceName,
                        platform: deviceInfo.platform
                    }
                },
                timestamp: Date.now(),
                senderId: this.signalingService.clientId || 'unknown',
                senderDeviceId: deviceInfo.deviceId,
                senderDeviceName: deviceInfo.deviceName,
                senderPlatform: deviceInfo.platform,
                senderPhoneNumber: deviceInfo.userPhoneNumber,
                senderUserName: deviceInfo.userName,
                senderDisplayName: deviceInfo.userDisplayName,
                senderAbbreviatedName: deviceInfo.abbreviatedName
            }, targetPeerId);
        }
    }

    async broadcastDataMessage(data: any, type: ChannelMessage['type'] = 'text'): Promise<void> {
        const deviceInfo = await this.deviceService.getDeviceInfo();

        const message: ChannelMessage = {
            type,
            data,
            timestamp: Date.now(),
            senderId: this.signalingService.clientId || 'unknown',
            senderDeviceId: deviceInfo.deviceId,
            senderDeviceName: deviceInfo.deviceName,
            senderPlatform: deviceInfo.platform,
            // Enhanced user identification
            senderPhoneNumber: deviceInfo.userPhoneNumber,
            senderUserName: deviceInfo.userName,
            senderDisplayName: deviceInfo.userDisplayName,
            senderAbbreviatedName: deviceInfo.abbreviatedName
        };

        // Special debug logging for audit requests
        if (type === 'system' && data?.type === 'audit-request') {
            console.log('🚨 AUDIT BROADCAST: Starting audit request broadcast', message);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT BROADCAST: Starting audit request broadcast`, message);

            const allPeers = Array.from(this.peers.values());
            const connectedPeers = this.getConnectedPeers();
            const healthyPeers = connectedPeers.filter(peer =>
                peer.connection?.connectionState === 'connected' &&
                peer.dataChannel?.readyState === 'open' &&
                peer.isConnected
            );

            console.log('🚨 AUDIT BROADCAST: Found', allPeers.length, 'total peers,', connectedPeers.length, 'connected,', healthyPeers.length, 'healthy');
            this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT BROADCAST: Found ${allPeers.length} total peers, ${connectedPeers.length} connected, ${healthyPeers.length} healthy`);

            // Clean up any unhealthy peers first
            const unhealthyPeers = allPeers.filter(peer =>
                !healthyPeers.includes(peer) &&
                (peer.connection?.connectionState === 'failed' ||
                    peer.connection?.connectionState === 'disconnected' ||
                    peer.connection?.connectionState === 'closed' ||
                    (peer.connection?.connectionState === 'connecting' && this.isConnectionStale(peer)))
            );

            if (unhealthyPeers.length > 0) {
                console.log('🚨 AUDIT BROADCAST: Cleaning up', unhealthyPeers.length, 'unhealthy peers before audit');
                this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT BROADCAST: Cleaning up ${unhealthyPeers.length} unhealthy peers before audit`);

                for (const peer of unhealthyPeers) {
                    this.cleanupOrphanedPeer(peer.id);
                }
            }

            let successfulSends = 0;
            let failedSends = 0;

            // Only send to healthy peers
            for (const peer of healthyPeers) {
                const peerState = {
                    id: peer.id,
                    deviceName: peer.deviceName,
                    hasDataChannel: !!peer.dataChannel,
                    dataChannelState: peer.dataChannel?.readyState,
                    connectionState: peer.connection?.connectionState,
                    isConnected: peer.isConnected
                };

                console.log('🚨 AUDIT BROADCAST: Checking peer', peer.deviceName || peer.id, ':', peerState);
                this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT BROADCAST: Checking peer ${peer.deviceName || peer.id}:`, peerState);

                try {
                    const jsonMessage = JSON.stringify(message);
                    peer.dataChannel!.send(jsonMessage);
                    successfulSends++;
                    console.log('🚨 AUDIT BROADCAST: ✅ Successfully sent to', peer.deviceName || peer.id);
                    this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT BROADCAST: ✅ Successfully sent to ${peer.deviceName || peer.id}`);
                } catch (error) {
                    failedSends++;
                    console.error('🚨 AUDIT BROADCAST: ❌ Failed to send to', peer.deviceName || peer.id, ':', error);
                    this.debugLoggerService.error('WebRTC Mesh', `🔍 AUDIT BROADCAST: ❌ Failed to send to ${peer.deviceName || peer.id}:`, error);
                }
            }

            console.log('🚨 AUDIT BROADCAST: Summary - Successful:', successfulSends, 'Failed:', failedSends, 'Healthy Peers:', healthyPeers.length);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT BROADCAST: Summary - Successful: ${successfulSends}, Failed: ${failedSends}, Healthy Peers: ${healthyPeers.length}`);
        }

        // Send to all peers
        this.sendDataMessage(message);

        // Also emit locally so the broadcaster can handle their own messages
        // This ensures that local scans update device count on the scanning device
        this._dataMessagesSignal.set(message);
        this.debugLoggerService.log('WebRTC Mesh', 'Broadcasted data message:', message);
    }

    /**
     * True if this device may change or broadcast Walky-Talky policy (event owner / admin devices only).
     */
    public async canBroadcastWalkyTalkyPolicy(): Promise<boolean> {
        if (this._isCreator) {
            return true;
        }
        const event = this.eventService.getCurrentEvent();
        if (!event?.creatorDeviceId) {
            return false;
        }
        const deviceInfo = await this.deviceService.getDeviceInfo();
        return deviceInfo.deviceId === event.creatorDeviceId;
    }

    public async broadcastWalkyTalkyPolicy(enabled: boolean, target: 'admins' | 'all', eventId?: string): Promise<void> {
        if (!(await this.canBroadcastWalkyTalkyPolicy())) {
            this.debugLoggerService.log('WebRTC Mesh', 'Skipping WalkyTalky policy broadcast: not authorized');
            return;
        }
        if (!this.isInitialized()) {
            this.debugLoggerService.log('WebRTC Mesh', 'Skipping WalkyTalky policy broadcast: mesh not initialized');
            return;
        }

        const resolvedEventId = eventId || this.currentEventId;
        if (!resolvedEventId) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Skipping WalkyTalky policy broadcast: no active event');
            return;
        }

        const ts = Date.now();
        await this.storage.set(this.WALKY_TALKY_POLICY_TS_KEY, String(ts));

        await this.broadcastDataMessage({
            type: 'walky_talky_settings_sync',
            eventId: resolvedEventId,
            enabled,
            target,
            policyTimestamp: ts
        }, 'system');
    }

    private async sendWalkyTalkyPolicyToPeer(peerId: string, eventId: string): Promise<void> {
        if (!(await this.canBroadcastWalkyTalkyPolicy())) {
            return;
        }
        const enabledRaw = await this.storage.get(this.WALKY_TALKY_ENABLED_KEY);
        const targetRaw = await this.storage.get(this.WALKY_TALKY_TARGET_KEY);
        const enabled = enabledRaw === null || enabledRaw === undefined ? false : String(enabledRaw) === 'true';
        const target: 'admins' | 'all' = targetRaw === 'all' ? 'all' : 'admins';
        const tsRaw = await this.storage.get(this.WALKY_TALKY_POLICY_TS_KEY);
        let policyTimestamp =
            tsRaw !== null && tsRaw !== undefined && String(tsRaw).length > 0
                ? parseInt(String(tsRaw), 10)
                : Date.now();
        if (!Number.isFinite(policyTimestamp)) {
            policyTimestamp = Date.now();
        }

        const deviceInfo = await this.deviceService.getDeviceInfo();

        const message: ChannelMessage = {
            type: 'system',
            data: {
                type: 'walky_talky_settings_sync',
                eventId,
                enabled,
                target,
                policyTimestamp
            },
            timestamp: Date.now(),
            senderId: this.signalingService.clientId || 'unknown',
            senderDeviceId: deviceInfo.deviceId,
            senderDeviceName: deviceInfo.deviceName,
            senderPlatform: deviceInfo.platform,
            senderPhoneNumber: deviceInfo.userPhoneNumber,
            senderUserName: deviceInfo.userName,
            senderDisplayName: deviceInfo.userDisplayName,
            senderAbbreviatedName: deviceInfo.abbreviatedName
        };

        this.sendDataMessage(message, peerId);
    }

    private isDevelopmentMode(): boolean {
        const isLocalhost = window.location.hostname === 'localhost';
        const isTestEnv = window.location.hostname.includes('test');
        const hasTestParam = window.location.search.includes('test=true');
        const isDevelopment = !window.location.protocol.startsWith('https') || isLocalhost;
        return isDevelopment || isTestEnv || hasTestParam;
    }

    /**
     * Broadcast device information to all connected peers
     */
    async broadcastDeviceInfo(): Promise<void> {
        const deviceInfo = await this.deviceService.getDeviceInfo();

        // Get current attendee count and device count from current event (if available)
        let attendeeCount: number = 0; // Initialize to 0
        let deviceCount: number = 0; // Initialize to 0
        try {
            const currentEvent = this.eventService.getCurrentEvent();
            if (currentEvent?.id) {
                // Ensure count is a number, default to 0 if null/undefined
                attendeeCount = (await this.attendanceService.getCount(currentEvent.id)) || 0;
                deviceCount = (await this.attendanceService.getDeviceCount(currentEvent.id)) ?? 0;
                this.debugLoggerService.log('WebRTC Mesh', `Broadcasting device info: attendeeCount=${attendeeCount}, deviceCount=${deviceCount}`);
            }
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', `Could not get counts for device info broadcast: ${error}. Defaulting to 0.`);
            attendeeCount = 0; // Ensure it's 0 on error as well
            deviceCount = 0; // Ensure it's 0 on error as well
        }

        const deviceInfoMessage = {
            deviceId: deviceInfo.deviceId,
            deviceName: deviceInfo.deviceName,
            platform: deviceInfo.platform,
            osVersion: deviceInfo.osVersion,
            manufacturer: deviceInfo.manufacturer,
            model: deviceInfo.model,
            // Enhanced user identification
            userPhoneNumber: deviceInfo.userPhoneNumber,
            userName: deviceInfo.userName,
            userDisplayName: deviceInfo.userDisplayName,
            abbreviatedName: deviceInfo.abbreviatedName,
            // Current count information
            attendeeCount,
            deviceCount, // NEW: Include device count for audit purposes
            isCreator: this._isCreator
        };

        // NEW: Send attendance update to WebSocket signaling server for historical tracking
        if (this.currentEventId) {
            this.sendAttendanceUpdateToServer(this.currentEventId, deviceInfo.deviceId, {
                attendeeCount,
                deviceCount,
                totalCount: attendeeCount // For manual events, total count = attendee count
            }, deviceInfo);
        }

        await this.broadcastDataMessage(deviceInfoMessage, 'device-info');
    }

    /**
     * Send attendance update to WebSocket signaling server for historical tracking
     */
    private sendAttendanceUpdateToServer(eventId: string, deviceId: string, attendanceData: any, deviceInfo: any): void {
        try {
            if (this.signalingService && this.signalingService.isConnected) {
                this.signalingService.sendAttendanceUpdate({
                    eventId,
                    deviceId,
                    deviceCount: attendanceData.deviceCount,
                    totalCount: attendanceData.totalCount,
                    deviceName: deviceInfo.deviceName,
                    platform: deviceInfo.platform,
                    userDisplayName: deviceInfo.userDisplayName
                });

                this.debugLoggerService.log('WebRTC Mesh', 'Sent attendance update to server', {
                    eventId,
                    deviceId,
                    attendeeCount: attendanceData.attendeeCount,
                    deviceCount: attendanceData.deviceCount
                });
            }
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', `Failed to send attendance update to server: ${error}`);
        }
    }

    /**
     * Broadcast current attendee count to all peers
     */
    async broadcastAttendeeCount(attendeeCount: number, eventId?: string): Promise<void> {
        // Use current event ID if not provided
        const targetEventId = eventId || this.currentEventId;

        if (!targetEventId) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Cannot broadcast attendee count: no event ID available');
            return;
        }

        const event = this.eventService.getEventById(targetEventId);
        if (event?.countMethod === 'qr') {
            // For QR events, never broadcast total counts; send full attendance sync instead.
            await this.broadcastAttendanceUpdate(targetEventId);
            return;
        }

        this.debugLoggerService.log('WebRTC Mesh', `Broadcasting attendee count: ${attendeeCount} for event: ${targetEventId}`);

        // NEW: Send attendance update to server before broadcasting to peers
        const deviceInfo = await this.deviceService.getDeviceInfo();
        const deviceCount = (await this.attendanceService.getDeviceCount(targetEventId)) ?? 0;
        this.sendAttendanceUpdateToServer(targetEventId, deviceInfo.deviceId, {
            attendeeCount: attendeeCount,
            deviceCount: deviceCount,
            totalCount: attendeeCount
        }, deviceInfo);

        // Get all connected peers for validation
        const connectedPeers = this.getConnectedPeers();
        const validPeers = connectedPeers.filter(p =>
            p.connection?.connectionState === 'connected' &&
            p.dataChannel?.readyState === 'open' &&
            p.isConnected
        );

        this.debugLoggerService.log('WebRTC Mesh', `Count broadcast validation: ${validPeers.length}/${connectedPeers.length} peers have valid connections`);

        // Identify peers with connection issues
        const problematicPeers = connectedPeers.filter(p =>
            !(p.connection?.connectionState === 'connected' &&
                p.dataChannel?.readyState === 'open' &&
                p.isConnected)
        );

        if (problematicPeers.length > 0) {
            this.debugLoggerService.warn('WebRTC Mesh', `Some peers may not receive count update:`,
                problematicPeers.map(p => ({
                    deviceName: p.deviceName || p.id,
                    connectionState: p.connection?.connectionState,
                    dataChannelState: p.dataChannel?.readyState,
                    isConnected: p.isConnected
                }))
            );
        }

        // Prepare count sync message
        const countSyncMessage = {
            type: 'count_sync',
            eventId: targetEventId,
            attendeeCount: attendeeCount,
            timestamp: Date.now(),
            senderId: this.signalingService.clientId || 'unknown'
        };

        try {
            // First attempt: standard broadcast
            await this.broadcastDataMessage(countSyncMessage, 'system');
            this.debugLoggerService.log('WebRTC Mesh', `Attendee count broadcast completed: ${attendeeCount}`);

            // If we have peers with connection issues, try individual sends with retry
            if (problematicPeers.length > 0) {
                this.debugLoggerService.log('WebRTC Mesh', `Attempting individual retry for ${problematicPeers.length} problematic peers`);

                for (const peer of problematicPeers) {
                    try {
                        // Wait a moment and check if connection improved
                        await new Promise(resolve => setTimeout(resolve, 1000));

                        if (peer.dataChannel?.readyState === 'open') {
                            await new Promise<void>((resolve, reject) => {
                                try {
                                    peer.dataChannel!.send(JSON.stringify({
                                        type: 'system',
                                        data: countSyncMessage,
                                        timestamp: Date.now(),
                                        senderId: this.signalingService.clientId || 'unknown'
                                    }));
                                    this.debugLoggerService.log('WebRTC Mesh', `Retry successful for peer: ${peer.deviceName || peer.id}`);
                                    resolve();
                                } catch (error) {
                                    this.debugLoggerService.warn('WebRTC Mesh', `Retry failed for peer ${peer.deviceName || peer.id}:`, error);
                                    reject(error);
                                }
                            });
                        } else {
                            this.debugLoggerService.warn('WebRTC Mesh', `Peer ${peer.deviceName || peer.id} still has problematic data channel: ${peer.dataChannel?.readyState}`);
                        }
                    } catch (error) {
                        this.debugLoggerService.warn('WebRTC Mesh', `Individual retry failed for peer ${peer.deviceName || peer.id}:`, error);
                    }
                }
            }

            // Broadcast device info to sync device counts (skip in lean mode to reduce cellular load)
            if (!this.shouldUseLeanMeshMode()) {
                await this.broadcastDeviceInfo();
            } else {
                this.debugLoggerService.log('WebRTC Mesh', 'Lean mode: skipped broadcastDeviceInfo after count sync');
            }

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to broadcast attendee count:`, error);
            throw error;
        }
    }

    /**
     * Broadcast peer count update to all peers
     */
    async broadcastPeerCountUpdate(): Promise<void> {
        const peerCount = this.getConnectedPeers().length + 1; // +1 for self
        this.debugLoggerService.log('WebRTC Mesh', `Broadcasting peer count update: ${peerCount}`);
        await this.broadcastDataMessage({ peerCount }, 'peer_count_update');
    }

    /**
     * Broadcast event data to all connected peers
     * This is used to share complete CounterEvent information when peers connect
     */
    async broadcastEventData(eventData: any): Promise<void> {
        this.debugLoggerService.log('WebRTC Mesh', 'Broadcasting event data to all WebRTC peers:', eventData);
        await this.broadcastDataMessage(eventData, 'system');
    }

    /**
     * Manual totals must be the sum of each device's own count.
     * Never add this device's count onto a peer's already-combined total.
     */
    private async sumKnownManualDeviceCounts(eventId: string): Promise<{ total: number; contributingPeers: number }> {
        const localCount = (await this.attendanceService.getDeviceCount(eventId)) ?? 0;
        const localDeviceId = this.deviceService.deviceId();
        const seen = new Set<string>();
        if (localDeviceId) {
            seen.add(localDeviceId);
        }

        let peerSum = 0;
        let contributingPeers = 0;
        for (const peer of this.peers.values()) {
            if (!peer.deviceId || seen.has(peer.deviceId)) {
                continue;
            }
            if (typeof peer.deviceCount !== 'number' || !Number.isFinite(peer.deviceCount)) {
                continue;
            }
            seen.add(peer.deviceId);
            peerSum += peer.deviceCount;
            contributingPeers++;
        }

        // Floor the shared total only; individual device counts may be negative.
        return { total: Math.max(0, localCount + peerSum), contributingPeers };
    }

    async reconcileManualTotal(eventId: string): Promise<number | null> {
        const event = this.eventService.getEventById(eventId);
        if (event?.countMethod !== 'manual') {
            return null;
        }

        const { total, contributingPeers } = await this.sumKnownManualDeviceCounts(eventId);
        if (contributingPeers === 0) {
            return null;
        }

        const currentTotal = this.attendanceService.count() || 0;
        if (currentTotal === total) {
            return total;
        }

        this.debugLoggerService.log('WebRTC Mesh', `Reconciling manual total from device counts: ${currentTotal} → ${total} (${contributingPeers} peer devices)`);
        await this.attendanceService.processRemoteTotalCountUpdate(eventId, total);
        return total;
    }

    /**
     * Broadcast current count state to new peers
     * For QR events: sends all scanned tickets/codes
     * For manual events: sends current count numbers
     */
    async broadcastCurrentCountState(eventId: string, targetPeerId?: string): Promise<void> {
        // Only broadcast if this device is the event creator
        if (!this._isCreator) {
            this.debugLoggerService.log('WebRTC Mesh', '⏩ Not broadcasting count state - only event creator can send full state sync.');
            return;
        }

        try {
            // Get current count state
            const totalCount = await this.attendanceService.getTotalCount(eventId);
            const deviceCount = (await this.attendanceService.getDeviceCount(eventId)) ?? 0;
            const deviceInfo = await this.deviceService.getDeviceInfo();

            // Get all tickets for QR events
            const tickets = await this.attendanceService.getTicketsForEvent(eventId);

            const countStateData = {
                type: 'count_state_sync',
                eventId,
                totalCount,
                deviceCount,
                deviceId: deviceInfo.deviceId,
                tickets, // Include all scanned QR codes for complete synchronization
                timestamp: Date.now(),
                senderId: this.signalingService.clientId || 'unknown',
                deviceInfo: {
                    deviceId: deviceInfo.deviceId,
                    deviceName: deviceInfo.userDisplayName || deviceInfo.deviceName
                }
            };

            this.debugLoggerService.log('WebRTC Mesh', 'Preparing to send count state sync:', countStateData); // NEW LOG
            this.debugLoggerService.log('WebRTC Mesh', `Sending count state sync to targetPeerId: ${targetPeerId || 'all peers'}`); // NEW LOG

            if (targetPeerId) {
                // Send to specific peer (for new connections)
                const peer = this.getPeer(targetPeerId);
                if (peer && peer.dataChannel && peer.dataChannel.readyState === 'open') {
                    peer.dataChannel.send(JSON.stringify({
                        type: 'system',
                        data: countStateData,
                        timestamp: Date.now(),
                        senderId: this.signalingService.clientId || 'unknown'
                    }));
                    this.debugLoggerService.log('WebRTC Mesh', `Sent count state sync to new peer: ${targetPeerId}`, {
                        totalCount,
                        ticketsCount: tickets.length
                    });
                }
            } else {
                // Broadcast to all peers
                await this.broadcastDataMessage(countStateData, 'system');
                this.debugLoggerService.log('WebRTC Mesh', `Broadcasted count state sync to all peers`, {
                    totalCount,
                    ticketsCount: tickets.length
                });
            }
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to broadcast count state: ${error}`);
        }
    }

    /**
     * Broadcast attendance update to all peers
     * This is used to share attendance updates (all Tickets) with all peers
     */
    async broadcastAttendanceUpdate(eventId: string, targetPeerId?: string): Promise<void> {
        const tickets = await this.attendanceService.getTicketsForEvent(eventId);
        if (tickets.length > 0) {
            const event = this.eventService.getEventById(eventId);
            if (event?.countMethod === 'qr' && !targetPeerId) {
                const localPresentCount = tickets.filter(ticket => ticket.isPresent).length;
                const serverCount = await this.serverSyncService.getServerCount(eventId);
                if (serverCount !== null && serverCount === localPresentCount) {
                    return;
                }
            }

            const attendanceUpdate: ChannelMessage = {
                type: 'system',
                timestamp: Date.now(),
                senderId: this.signalingService.clientId || 'unknown',
                data: {
                    type: 'full_attendance_sync', // Changed from 'attendance_update'
                    eventId,
                    tickets
                }
            };
            this.debugLoggerService.log('WebRTC Mesh', `Broadcast Tickets for event:`, attendanceUpdate);
            if (targetPeerId) {
                this.debugLoggerService.log('WebRTC Mesh', `Broadcasting all tickets for event to specific peer: ${targetPeerId}`, attendanceUpdate);
                await this.sendDataMessage(attendanceUpdate, targetPeerId);
            } else {
                this.debugLoggerService.log('WebRTC Mesh', `Broadcasting all tickets for event to all WebRTC peers:`, attendanceUpdate);
                await this.broadcastDataMessage(attendanceUpdate, 'system');
            }
        }
    }

    /**
     * Set a callback that will be triggered when a new peer connects
     * This allows other components to automatically share data with new peers
     */
    private onPeerConnectedCallback?: (peerId: string) => void;

    setOnPeerConnectedCallback(callback: (peerId: string) => void): void {
        this.onPeerConnectedCallback = callback;
    }

    clearOnPeerConnectedCallback(): void {
        this.onPeerConnectedCallback = undefined;
    }

    /**
     * Join an event room for private event-based connections
     */
    async joinEventRoom(eventId: string, eventPassword?: string): Promise<void> {
        if (!this.signalingService.isConnected) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Signaling not connected, attempting to connect before joining event');
            await this.signalingService.connect();
        }

        await this.signalingService.joinEventRoom(eventId, eventPassword || '');
        this.debugLoggerService.log('WebRTC Mesh', `WebRTC mesh service joined event room: ${eventId}`);
    }

    /**
     * Cache important messages for late-joining subscribers
     */
    private cacheMessage(message: ChannelMessage): void {
        // Only cache system messages that might contain event data
        if (message.type === 'system' && message.data) {
            if (!this._cachedMessages.has('system')) {
                this._cachedMessages.set('system', []);
            }

            const cached = this._cachedMessages.get('system')!;
            cached.push(message);

            // Keep only the last 5 messages to prevent memory bloat
            if (cached.length > 5) {
                cached.shift();
            }

            this.debugLoggerService.log('WebRTC Mesh', 'Cached system message for late subscribers:', message);
        }
    }

    /**
     * Get cached messages of a specific type
     */
    getCachedMessages(type: string): ChannelMessage[] {
        return this._cachedMessages.get(type) || [];
    }

    /**
     * Clear cached messages
     */
    clearCachedMessages(): void {
        this.debugLoggerService.log('WebRTC Mesh', 'Cleared cached messages');
    }

    // Legacy compatibility method
    broadcastMessage(message: any, excludePeerId?: string): void {
        const channelMessage: ChannelMessage = {
            type: 'text',
            data: message,
            timestamp: Date.now(),
            senderId: this.signalingService.clientId || 'unknown'
        };

        if (excludePeerId) {
            const peers = Array.from(this.peers.values()).filter(p => p.id !== excludePeerId);
            peers.forEach(peer => {
                if (peer?.dataChannel && peer.dataChannel.readyState === 'open') {
                    try {
                        peer.dataChannel.send(JSON.stringify(channelMessage));
                    } catch (error) {
                        this.debugLoggerService.error('WebRTC Mesh', `Failed to send message to ${peer.id}: ${error}`);
                    }
                }
            });
        } else {
            this.sendDataMessage(channelMessage);
        }
        this.debugLoggerService.log('WebRTC Mesh', 'Broadcasted legacy message:', channelMessage);
    }

    getLocalStream(): MediaStream | undefined {
        return this.localStream;
    }

    async addLocalStream(stream: MediaStream): Promise<void> {
        // Store the stream
        this.localStream = stream;

        // Add tracks to all existing peer connections
        this.peers.forEach(peer => {
            stream.getTracks().forEach(track => {
                // Check if track already exists
                const existingSender = peer.connection.getSenders().find(sender =>
                    sender.track && sender.track.kind === track.kind
                );

                if (existingSender) {
                    // Replace existing track
                    existingSender.replaceTrack(track);
                } else {
                    // Add new track
                    peer.connection.addTrack(track, stream);
                }
            });

            // Renegotiate with all connected peers so the remote side receives the new track via ontrack
            if (peer.isConnected) {
                this.createOffer(peer);
            }
        });
        this.debugLoggerService.log('WebRTC Mesh', 'Added local stream to peer connections');
    }

    getPeer(peerId: string): WebRTCPeer | undefined {
        return this.peers.get(peerId);
    }

    getConnectedPeers(): WebRTCPeer[] {
        return Array.from(this.peers.values()).filter(peer => peer.isConnected);
    }

    // Legacy compatibility methods
    getConnectedPeerIds(): string[] {
        return this.getConnectedPeers().map(peer => peer.id);
    }

    isInitialized(): boolean {
        return this._meshStatusSignal();
    }

    // Legacy compatibility methods
    disconnectAll(): void {
        this.debugLoggerService.log('WebRTC Mesh', 'Disconnecting from all peers...');

        for (const [peerId, peer] of this.peers.entries()) {
            this.cleanupOrphanedPeer(peerId);
        }

        this.peers.clear();
        this.lastSeenMap.clear();
        this.peerRttMap.clear();
        this._meshStatusSignal.set(false);
        this.updateConnectedPeers();
        this.debugLoggerService.log('WebRTC Mesh', 'All peers disconnected');
    }

    cleanupMesh(): void {
        this.debugLoggerService.log('WebRTC Mesh', 'Cleaning up WebRTC mesh...');

        // Stop health monitoring
        this.stopHealthMonitoring();

        // Disconnect all peers
        this.disconnectAll();

        // Clear cached messages
        this.clearCachedMessages();
        this.debugLoggerService.log('WebRTC Mesh', 'WebRTC mesh cleanup complete');
    }

    async destroy(): Promise<void> {
        this.debugLoggerService.log('WebRTC Mesh', 'Destroying WebRTC mesh...');

        this.destroy$.next();
        this.destroy$.complete();

        // Stop health monitoring
        this.stopHealthMonitoring();

        // Close all peer connections
        this.peers.forEach(peer => {
            peer.connection.close();
        });
        this.peers.clear();

        // Clear tracking maps
        this.lastSeenMap.clear();
        this.peerRttMap.clear();

        // Stop local stream
        if (this.localStream) {
            this.localStream.getTracks().forEach(track => track.stop());
            this.localStream = undefined;
        }

        // Disconnect from signaling server
        this.signalingService.disconnect();
        this._meshStatusSignal.set(false);
        this.updateConnectedPeers();
        this.debugLoggerService.log('WebRTC Mesh', 'WebRTC mesh destroyed');
    }

    private setupSignalingHandlers(): void {
        this.signalingService.messages$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(message => {
            this.handleSignalingMessage(message);
        });

        // Send device identification when connected
        this.signalingService.connectionStatus$.subscribe(async (connected) => {
            if (connected) {
                this.debugLoggerService.log('WebRTC Mesh', 'Signaling connected, sending device identification');
                await this.sendDeviceIdentification();
            }
        });
    }

    private async handleSignalingMessage(message: any): Promise<void> {
        try {
            switch (message.type) {
                case 'existing-peers':
                case 'event-peers':
                    if (message.peers) {
                        this.debugLoggerService.log('WebRTC Mesh', `Received existing peers:`, message.peers);
                        for (const peer of message.peers) {
                            const peerId = this.signalingPeerClientId(peer);
                            if (!peerId) {
                                continue;
                            }
                            this.debugLoggerService.log('WebRTC Mesh', `Attempting to connect to existing peer: ${peerId}`);
                            await this.connectToPeer(peerId);
                        }
                    }
                    break;

                case 'peer-joined':
                    if (message.clientId && message.clientId !== this.signalingService.clientId) {
                        this.debugLoggerService.log('WebRTC Mesh', `New peer joined: ${message.clientId}, attempting connection`);
                        await this.connectToPeer(message.clientId);
                    } else if (message.clientId === this.signalingService.clientId) {
                        this.debugLoggerService.log('WebRTC Mesh', `Received peer-joined message for self (${message.clientId}), ignoring`);
                    }
                    break;

                case 'offer':
                    if (message.fromId && message.data) {
                        await this.handleOffer(message.fromId, message.data);
                    }
                    break;

                case 'answer':
                    if (message.fromId && message.data) {
                        await this.handleAnswer(message.fromId, message.data);
                    }
                    break;

                case 'ice-candidate':
                    if (message.fromId && message.data) {
                        await this.handleIceCandidate(message.fromId, message.data);
                    }
                    break;

                case 'peer-left':
                    if (message.clientId) {
                        this.disconnectFromPeer(message.clientId);
                    }
                    break;
                case 'admin-disconnect': {
                    if (!message?.eventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    if (message.eventId !== this.currentEventId) break;
                    if (message.discount) {
                        this.markDecreaseAllowed(message.eventId, 'admin-discount');
                    }
                    await this.setReconnectBlocked(message.eventId, true);
                    await this.showAdminDisconnectNotification();
                    this.destroy();
                    break;
                }
                case 'admin-remove-event': {
                    if (!message?.eventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    await this.setReconnectBlocked(message.eventId, true);
                    await this.showAdminRemoveEventNotification();
                    await this.eventService.removeEventLocal(message.eventId);
                    this.destroy();
                    break;
                }
                case 'admin-adopt-event': {
                    if (!message?.ownerEventId || !message?.duplicateEventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;

                    let ownerEvent = this.eventService.getEventById(message.ownerEventId);
                    if (!ownerEvent) {
                        const parsedDate = message.ownerEventDate ? new Date(message.ownerEventDate) : new Date();
                        ownerEvent = this.eventService.createEvent({
                            id: message.ownerEventId,
                            name: message.ownerEventName || `Event ${message.ownerEventId}`,
                            date: Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate,
                            countMethod: 'qr',
                            ticketFormat: message.ticketFormat || 'standard',
                            venueCode: message.venueCode || undefined,
                            zoneCode: message.zoneCode || undefined,
                            isSharedEvent: true,
                            creatorDeviceId: deviceInfo.deviceId
                        });
                    }

                    if (message.ownerEventPassword) {
                        this.eventService.updateEventPassword(message.ownerEventId, message.ownerEventPassword);
                    }

                    this.eventService.markEventAsShared(message.ownerEventId, deviceInfo.deviceId);

                    if (message.duplicateEventId !== message.ownerEventId) {
                        await this.eventService.removeEventLocal(message.duplicateEventId);
                    }

                    await this.eventService.setCurrentEvent(message.ownerEventId);
                    await this.initMesh(message.ownerEventId, true, message.ownerEventPassword || ownerEvent?.password || '');
                    await this.showApprovedAdoptionNotification(message.ownerEventName || message.ownerEventId);
                    break;
                }
                case 'admin-duplicate-access-request': {
                    if (!message?.ownerEventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    const requestId = Number(message.requestId || 0);
                    const lastSeenAt = this.ownerConflictAlertSeenAt.get(requestId);
                    if (requestId && lastSeenAt && (Date.now() - lastSeenAt) < 60_000) {
                        break;
                    }
                    if (requestId) {
                        this.ownerConflictAlertSeenAt.set(requestId, Date.now());
                    }
                    await this.showOwnerConflictAlert({
                        ownerEventId: message.ownerEventId,
                        ownerEventName: message.ownerEventName || message.ownerEventId,
                        requesterLabel: message.requesterUserDisplayName || message.requesterDeviceName || message.requesterDeviceId || 'Another device'
                    });
                    break;
                }
                case 'admin-reconnect-blocked': {
                    if (!message?.eventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    await this.setReconnectBlocked(message.eventId, true);
                    await this.showReconnectBlockedNotification(true);
                    break;
                }
                case 'admin-reconnect-allowed': {
                    if (!message?.eventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    await this.setReconnectBlocked(message.eventId, false);
                    await this.showReconnectBlockedNotification(false);
                    break;
                }
                case 'admin-set-password': {
                    if (!message?.eventId || !message?.deviceId || !message?.eventPassword) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    this.eventService.updateEventPassword(message.eventId, message.eventPassword);
                    break;
                }

                case 'reconnection-available':
                    this.debugLoggerService.log('WebRTC Mesh', `🔌 Reconnection available for previous event: ${message.eventId}`);
                    this.debugLoggerService.log('WebRTC Mesh', '🔌 Current mesh status before reconnection:', {
                        isInitialized: this.isInitialized(),
                        connectedPeers: this.getConnectedPeers().length,
                        meshStatus: this._meshStatusSignal()
                    });

                    // Automatically reconnect to the previous event if available
                    if (message.eventId && message.activePeers > 0) {
                        if (await this.isReconnectBlocked(message.eventId)) {
                            this.debugLoggerService.log('WebRTC Mesh', `Auto-reconnect blocked for event ${message.eventId}`);
                            await this.showReconnectBlockedNotification(true);
                            break;
                        }
                        this.debugLoggerService.log('WebRTC Mesh', `🔌 Auto-reconnecting to event ${message.eventId} with ${message.activePeers} active peers`);

                        // Automatically reconnect without user prompts
                        this.reconnectToEvent(message.eventId).then(success => {
                            this.debugLoggerService.log('WebRTC Mesh', `🔌 Reconnection attempt completed: ${success}`, {
                                eventId: message.eventId,
                                activePeers: message.activePeers,
                                currentPeers: this.getConnectedPeers().length,
                                meshStatus: this._meshStatusSignal()
                            });

                            if (success) {
                                this.debugLoggerService.log('WebRTC Mesh', `✅ Successfully auto-reconnected to event: ${message.eventId}`);

                                // Emit an event that components can listen to for auto-navigation
                                this._eventsSignal.set({
                                    type: 'auto-reconnected',
                                    peerId: 'system',
                                    data: {
                                        eventId: message.eventId,
                                        activePeers: message.activePeers,
                                        autoReconnected: true
                                    }
                                });

                                this.debugLoggerService.log('WebRTC Mesh', '🔌 Auto-reconnection event emitted to components');
                            } else {
                                this.debugLoggerService.log('WebRTC Mesh', `❌ Auto-reconnection failed for event: ${message.eventId}`);
                            }
                        }).catch(error => {
                            this.debugLoggerService.error('WebRTC Mesh', `🔌 Auto-reconnection error for event ${message.eventId}: ${error}`);
                        });
                    } else {
                        this.debugLoggerService.log('WebRTC Mesh', `🔌 Reconnection available but no active peers or missing eventId:`, {
                            hasEventId: !!message.eventId,
                            activePeers: message.activePeers
                        });
                    }
                    break;
                case 'set-creator': {
                    if (!message?.eventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    const event = this.eventService.getEventById(message.eventId);
                    if (!event) break;
                    this.eventService.markEventAsShared(event.id, deviceInfo.deviceId);
                    await this.initMesh(event.id, true, event.password || '');
                    break;
                }
                case 'revoke-creator': {
                    if (!message?.eventId || !message?.deviceId) break;
                    const deviceInfo = await this.deviceService.getDeviceInfo();
                    if (deviceInfo.deviceId !== message.deviceId) break;
                    const event = this.eventService.getEventById(message.eventId);
                    if (!event) break;
                    this.eventService.markEventAsShared(event.id, null);
                    await this.initMesh(event.id, false, event.password || '');
                    break;
                }
                case 'server-total-update':
                    if (message.eventId && typeof message.totalCount === 'number') {
                        const event = this.eventService.getEventById(message.eventId);
                        if (event?.countMethod === 'manual') {
                            this.markDecreaseAllowed(message.eventId, 'server-total-update');
                            await this.attendanceService.processRemoteTotalCountUpdate(message.eventId, message.totalCount);
                        }
                    }
                    break;
                case 'admin-discount': {
                    if (!message?.eventId) break;
                    this.markDecreaseAllowed(message.eventId, 'admin-discount');
                    break;
                }

                case 'event-status-response':
                    this.debugLoggerService.log('WebRTC Mesh', `Received event status response: ${message.eventId}`, message.status);
                    // This is handled by the subscription in checkEventStatusViaWebSocket
                    // No action needed here as it's processed by the promise resolver
                    break;

                case 'request-full-attendance-sync':
                case 'reconnection-check': // NEW: Handle reconnection-check as a request for full attendance sync
                    this.debugLoggerService.log('WebRTC Mesh', `Processing ${message.type} request. isCreator: ${this._isCreator}, eventId: ${message.eventId}`);
                    if (this._isCreator && (message.senderId || message.requesterId) && message.eventId) {
                        const eventId = message.eventId;
                        const currentEvent = this.eventService.getEventById(eventId);
                        if (currentEvent && currentEvent.countMethod === 'qr') {
                            this.debugLoggerService.log('WebRTC Mesh', `Broadcasting full attendance update for event ${eventId}`);
                            await this.broadcastAttendanceUpdate(eventId, message.senderId || message.requesterId);
                        } else {
                            this.debugLoggerService.log('WebRTC Mesh', `Skipping attendance broadcast for event ${eventId} - not a QR event`);
                        }
                    } else {
                        this.debugLoggerService.warn('WebRTC Mesh', 'Ignoring request - not creator or missing eventId/senderId');
                    }
                    break;

                case 'sync-request':
                    this.debugLoggerService.log('WebRTC Mesh', `Received sync-request from peer`, message);
                    await this.handleSyncRequest(message);
                    break;

                case 'sync-response':
                    this.debugLoggerService.log('WebRTC Mesh', `Received sync-response from peer`, message);
                    this.handleSyncResponse(message);
                    break;

                default:
                    this.logUnhandledSignalingType(message);
            }
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Error handling signaling message: ${error}`);
        }
    }

    /**
     * Request audit data from all connected peers (Creator only)
     */
    async requestAuditFromAllPeers(eventId: string): Promise<{
        success: boolean;
        auditResults?: Array<{
            deviceId: string;
            deviceName: string;
            platform: string;
            totalCount: number;
            deviceCount: number;
            timestamp: number;
            userDisplayName?: string;
        }>;
        error?: string;
    }> {
        try {
            console.log('🚨 AUDIT START: requestAuditFromAllPeers called with eventId:', eventId);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Requesting audit data from all peers for event: ${eventId}`);

            // NEW: Debug ALL peers vs connected peers
            const allPeersInMesh = Array.from(this.peers.keys());
            const connectedPeers = this.getConnectedPeers();

            // NEW: Check signaling service peer count
            const signalingPeers = this.signalingService.peersSignal();
            const signalingDevices = this.signalingService.connectedDevicesSignal();

            console.log('🚨 PEER ANALYSIS: ALL peers in mesh:', allPeersInMesh.length, allPeersInMesh);
            console.log('🚨 PEER ANALYSIS: CONNECTED peers:', connectedPeers.length, connectedPeers.map(p => ({
                id: p.id,
                deviceName: p.deviceName,
                isConnected: p.isConnected,
                connectionState: p.connection?.connectionState,
                dataChannelState: p.dataChannel?.readyState
            })));
            console.log('🚨 SIGNALING ANALYSIS: Signaling peers:', signalingPeers.length, signalingPeers);
            console.log('🚨 SIGNALING ANALYSIS: Signaling devices:', signalingDevices.size, Array.from(signalingDevices.entries()));

            // NEW: PWA-specific analysis
            console.log('🚨 PWA ANALYSIS: Current user agent:', navigator.userAgent);
            console.log('🚨 PWA ANALYSIS: Is this device a PWA?', window.matchMedia('(display-mode: standalone)').matches);

            // Check if any signaling peers are missing from WebRTC mesh
            const missingFromWebRTC = signalingPeers.filter(sigPeerId =>
                !allPeersInMesh.includes(sigPeerId)
            );

            if (missingFromWebRTC.length > 0) {
                console.log('🚨 SIGNALING->WEBRTC GAP:', missingFromWebRTC.length, 'peers in signaling but NOT in WebRTC mesh:', missingFromWebRTC);
                missingFromWebRTC.forEach(peerId => {
                    const deviceInfo = signalingDevices.get(peerId);
                    console.log('🚨 MISSING FROM WEBRTC:', peerId, {
                        deviceInfo: deviceInfo,
                        platform: deviceInfo?.platform,
                        deviceName: deviceInfo?.deviceName,
                        isProbablyPWA: deviceInfo?.platform === 'web' || deviceInfo?.platform === 'pwa'
                    });
                });
            }

            // Find disconnected/problematic peers
            const disconnectedPeerIds = allPeersInMesh.filter(peerId =>
                !connectedPeers.some(cp => cp.id === peerId)
            );

            if (disconnectedPeerIds.length > 0) {
                console.log('🚨 MISSING PEERS:', disconnectedPeerIds.length, 'peers in mesh but not connected:');
                disconnectedPeerIds.forEach(peerId => {
                    const peer = this.peers.get(peerId);
                    console.log('🚨 MISSING PEER:', peerId, {
                        deviceName: peer?.deviceName || 'Unknown',
                        isConnected: peer?.isConnected,
                        connectionState: peer?.connection?.connectionState,
                        dataChannelState: peer?.dataChannel?.readyState,
                        hasConnection: !!peer?.connection,
                        hasDataChannel: !!peer?.dataChannel
                    });
                });
            }

            console.log('🚨 AUDIT START: Found', connectedPeers.length, 'connected peers');
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Total connected peers found: ${connectedPeers.length}`);

            // Detailed analysis of each peer's connection state
            connectedPeers.forEach((peer, index) => {
                const connectionState = peer.connection?.connectionState;
                const dataChannelState = peer.dataChannel?.readyState;
                const isConnected = peer.isConnected;

                this.debugLoggerService.log('WebRTC Mesh', `🔍 Peer ${index + 1}: ${peer.deviceName || peer.id}`, {
                    id: peer.id,
                    deviceName: peer.deviceName,
                    deviceId: peer.deviceId,
                    connectionState,
                    dataChannelState,
                    isConnected,
                    hasDataChannel: !!peer.dataChannel,
                    hasConnection: !!peer.connection
                });
            });

            // Filter to only peers with open data channels
            const validPeers = connectedPeers.filter(p => {
                const isValid = p.connection?.connectionState === 'connected' &&
                    p.dataChannel?.readyState === 'open' &&
                    p.isConnected;

                if (!isValid) {
                    this.debugLoggerService.warn('WebRTC Mesh', `🔍 Excluding peer ${p.deviceName || p.id} from audit:`, {
                        reason: !p.connection ? 'No connection object' :
                            p.connection.connectionState !== 'connected' ? `Connection state: ${p.connection.connectionState}` :
                                !p.dataChannel ? 'No data channel' :
                                    p.dataChannel.readyState !== 'open' ? `Data channel state: ${p.dataChannel.readyState}` :
                                        !p.isConnected ? 'isConnected flag false' : 'Unknown',
                        connectionState: p.connection?.connectionState,
                        dataChannelState: p.dataChannel?.readyState,
                        isConnected: p.isConnected
                    });
                }

                return isValid;
            });

            this.debugLoggerService.log('WebRTC Mesh', `🔍 Valid peers for audit: ${validPeers.length}/${connectedPeers.length}`);
            validPeers.forEach((peer, index) => {
                this.debugLoggerService.log('WebRTC Mesh', `🔍 Valid peer ${index + 1}: ${peer.deviceName || peer.id} (${peer.deviceId || peer.id})`);
            });

            if (validPeers.length === 0) {
                this.debugLoggerService.warn('WebRTC Mesh', `🔍 No peers with valid connections to audit`);
                return {
                    success: false,
                    error: 'No peers with valid connections to audit'
                };
            }

            // Send audit request to all peers
            const auditMessage = {
                type: 'audit-request',
                eventId,
                timestamp: Date.now(),
                requesterId: this.signalingService.clientId
            };

            this.debugLoggerService.log('WebRTC Mesh', `🔍 Broadcasting audit request to ${validPeers.length} valid peers:`, auditMessage);
            await this.broadcastDataMessage(auditMessage, 'system');
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Audit request broadcast completed`);

            // Wait for responses (with longer timeout for slower devices)
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Starting to collect responses from ${validPeers.length} valid peers (30s timeout)`);

            // Check for newly connected peers during collection period
            const initialPeerCount = validPeers.length;
            const auditResults = await this.collectAuditResponses(eventId, initialPeerCount);

            // Check if new peers connected during audit and we should wait for them
            const currentValidPeers = this.getConnectedPeers().filter(p =>
                p.connection?.connectionState === 'connected' &&
                p.dataChannel?.readyState === 'open' &&
                p.isConnected
            );

            if (currentValidPeers.length > initialPeerCount && auditResults.length < currentValidPeers.length) {
                this.debugLoggerService.log('WebRTC Mesh', `🔍 Found ${currentValidPeers.length - initialPeerCount} new peers connected during audit. Extending collection...`);

                // Send audit request to newly connected peers
                const newPeers = currentValidPeers.filter(currentPeer =>
                    !validPeers.some(initialPeer => initialPeer.id === currentPeer.id)
                );

                if (newPeers.length > 0) {
                    this.debugLoggerService.log('WebRTC Mesh', `🔍 Sending audit request to ${newPeers.length} newly connected peers`);
                    for (const peer of newPeers) {
                        try {
                            const auditMessage = {
                                type: 'system' as const,
                                data: {
                                    type: 'audit-request',
                                    eventId,
                                    timestamp: Date.now(),
                                    requesterId: this.signalingService.clientId
                                },
                                timestamp: Date.now(),
                                senderId: this.signalingService.clientId || 'unknown'
                            };
                            this.sendDataMessage(auditMessage, peer.id);
                        } catch (error) {
                            this.debugLoggerService.warn('WebRTC Mesh', `Failed to send audit to newly connected peer ${peer.id}:`, error);
                        }
                    }

                    // Wait additional time for new responses (using same timeout as main collection)
                    const additionalResults = await this.collectAuditResponses(eventId, newPeers.length);
                    auditResults.push(...additionalResults);
                }
            }

            this.debugLoggerService.log('WebRTC Mesh', `🔍 Final audit response collection completed. Received ${auditResults.length}/${currentValidPeers.length} responses`);

            // Log final audit participation summary
            const participationRate = auditResults.length / validPeers.length;
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Audit Summary:`, {
                totalConnectedPeers: connectedPeers.length,
                validPeersForAudit: validPeers.length,
                responsesReceived: auditResults.length,
                participationRate: `${auditResults.length}/${validPeers.length} (${Math.round(participationRate * 100)}%)`
            });

            // Auto-cleanup if participation rate is low (< 75%) and we have stale peers
            if (participationRate < 0.75 && connectedPeers.length > validPeers.length) {
                const staleCount = connectedPeers.length - validPeers.length;
                this.debugLoggerService.log('WebRTC Mesh', `🔍 Low participation rate (${Math.round(participationRate * 100)}%) with ${staleCount} stale peers detected. Triggering automatic cleanup...`);

                const cleanedCount = this.cleanupStalePeers();
                if (cleanedCount > 0) {
                    this.debugLoggerService.log('WebRTC Mesh', `🔍 Automatic cleanup removed ${cleanedCount} stale peers to improve audit reliability`);
                }
            }

            return {
                success: true,
                auditResults
            };
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `🔍 Failed to request audit from peers:`, error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Collect audit responses from peers
     */
    private async collectAuditResponses(eventId: string, expectedResponses: number): Promise<Array<{
        deviceId: string;
        deviceName: string;
        platform: string;
        totalCount: number;
        deviceCount: number;
        timestamp: number;
        userDisplayName?: string;
    }>> {
        console.log(`🚨 AUDIT COLLECTION: 🚀 Starting response collection for event ${eventId}, expecting ${expectedResponses} responses`);
        this.debugLoggerService.log('WebRTC Mesh', `🔍 Starting audit response collection for event ${eventId}, expecting ${expectedResponses} responses`);

        return new Promise((resolve) => {
            const responses: Array<any> = [];
            const receivedDeviceIds = new Set<string>(); // Track unique devices
            let isCollecting = true;

            const cleanup = () => {
                isCollecting = false;
                this.auditResponseHandler = null;
            };

            const timeout = setTimeout(() => {
                console.log(`🚨 AUDIT COLLECTION: ⏰ Timeout after 30s. Received ${responses.length}/${expectedResponses} responses`);
                this.debugLoggerService.log('WebRTC Mesh', `🔍 Audit collection timeout (30s). Received ${responses.length}/${expectedResponses} responses:`, responses.map(r => r.deviceName || r.deviceId));

                // Debug missing responses
                if (responses.length < expectedResponses) {
                    const connectedPeers = this.getConnectedPeers();
                    const missingDevices = connectedPeers.filter(p => !receivedDeviceIds.has(p.deviceId || p.id));
                    console.log('🚨 AUDIT COLLECTION: ❌ Missing responses from devices:', missingDevices.map(p => p.deviceName || p.id));
                    this.debugLoggerService.warn('WebRTC Mesh', `🔍 Missing responses from devices:`, missingDevices.map(p => ({
                        id: p.id,
                        deviceName: p.deviceName,
                        deviceId: p.deviceId,
                        connectionState: p.connection?.connectionState,
                        dataChannelState: p.dataChannel?.readyState,
                        isConnected: p.isConnected
                    })));
                }

                cleanup();
                resolve(responses);
            }, 30000);

            // Set up simple message handler for audit responses
            this.auditResponseHandler = (message: ChannelMessage) => {
                if (!isCollecting) return;

                // Only process audit responses for this specific event
                if (message.type === 'system' &&
                    message.data?.type === 'audit-response' &&
                    message.data?.eventId === eventId) {

                    console.log(`🚨 AUDIT COLLECTION: 📨 Received audit-response:`, {
                        eventId: message.data?.eventId,
                        deviceId: message.data?.deviceId,
                        deviceName: message.data?.deviceName,
                        deviceCount: message.data?.deviceCount,
                        totalCount: message.data?.totalCount,
                        alreadyReceived: receivedDeviceIds.has(message.data?.deviceId)
                    });

                    // Avoid duplicate responses from the same device
                    if (!receivedDeviceIds.has(message.data.deviceId)) {
                        receivedDeviceIds.add(message.data.deviceId);
                        responses.push(message.data);

                        console.log(`🚨 AUDIT COLLECTION: ✅ Received response ${responses.length}/${expectedResponses} from ${message.data.deviceName || message.data.deviceId}`);
                        this.debugLoggerService.log('WebRTC Mesh', `🔍 Received audit response ${responses.length}/${expectedResponses} from device: ${message.data.deviceName || message.data.deviceId} (${message.data.deviceId})`);

                        if (responses.length >= expectedResponses) {
                            console.log(`🚨 AUDIT COLLECTION: 🎉 All ${responses.length}/${expectedResponses} responses collected!`);
                            this.debugLoggerService.log('WebRTC Mesh', `🔍 All audit responses collected (${responses.length}/${expectedResponses})`);
                            clearTimeout(timeout);
                            cleanup();
                            resolve(responses);
                        }
                    } else {
                        console.log(`🚨 AUDIT COLLECTION: 🔄 Ignoring duplicate response from ${message.data.deviceName || message.data.deviceId}`);
                        this.debugLoggerService.log('WebRTC Mesh', `🔍 Ignoring duplicate audit response from device: ${message.data.deviceName || message.data.deviceId} (${message.data.deviceId})`);
                    }
                }
            };

            // Manual trigger to test if we have cached responses
            setTimeout(() => {
                const currentMessage = this._dataMessagesSignal();
                if (currentMessage && this.auditResponseHandler) {
                    console.log('🚨 AUDIT COLLECTION: 🔍 Checking for immediate response in signal:', currentMessage.type, currentMessage.data?.type);
                    this.auditResponseHandler(currentMessage);
                }
            }, 100);

            // Safety cleanup  
            setTimeout(() => {
                console.log(`🚨 AUDIT COLLECTION: 🛡️ Safety cleanup triggered after 31s`);
                cleanup();
            }, 31000); // 31 seconds for safety (30s + 1s buffer)
        });
    }

    // Simplified message handler
    private auditResponseHandler: ((message: ChannelMessage) => void) | null = null;

    // Track recent audit requests to prevent duplicate processing
    private recentAuditRequests = new Map<string, number>();
    // Track audit responses sent to prevent duplicate responses
    private sentAuditResponses = new Map<string, number>();

    /**
     * Handle incoming audit request (Peer devices)
     */
    private async handleAuditRequest(auditRequest: any): Promise<void> {
        try {
            const { eventId, requesterId, timestamp } = auditRequest;
            console.log('🚨 AUDIT REQUEST: Received audit request for event', eventId, 'from', requesterId);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Received audit request for event ${eventId} from ${requesterId}`);

            // Check for duplicate requests (within 5 seconds)
            const requestKey = `${eventId}-${requesterId}`;
            const lastProcessed = this.recentAuditRequests.get(requestKey);
            const now = Date.now();

            if (lastProcessed && (now - lastProcessed) < 5000) {
                console.log('🚨 AUDIT REQUEST: ⚠️ Ignoring duplicate audit request from', requesterId, 'within 5 seconds');
                this.debugLoggerService.log('WebRTC Mesh', `🔍 Ignoring duplicate audit request from ${requesterId} within 5 seconds`);
                return;
            }

            // Check if we already sent a response for this event recently (within 10 seconds)
            const responseKey = `${eventId}-response`;
            const lastResponseSent = this.sentAuditResponses.get(responseKey);

            if (lastResponseSent && (now - lastResponseSent) < 10000) {
                console.log('🚨 AUDIT REQUEST: ⚠️ Already sent audit response for event', eventId, 'within 10 seconds, skipping');
                this.debugLoggerService.log('WebRTC Mesh', `🔍 Already sent audit response for event ${eventId} within 10 seconds, skipping duplicate`);
                return;
            }

            // Record this request to prevent future duplicates
            this.recentAuditRequests.set(requestKey, now);
            this.sentAuditResponses.set(responseKey, now);

            // Clean up old entries (older than 30 seconds)
            for (const [key, time] of this.recentAuditRequests.entries()) {
                if (now - time > 30000) {
                    this.recentAuditRequests.delete(key);
                }
            }
            for (const [key, time] of this.sentAuditResponses.entries()) {
                if (now - time > 30000) {
                    this.sentAuditResponses.delete(key);
                }
            }

            console.log('🚨 AUDIT RESPONSE: Processing audit request for event', eventId, 'from', requesterId);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Processing audit request for event ${eventId} from ${requesterId}`);

            // Get current count data
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Getting count data for event: ${eventId}`);
            const totalCount = await this.attendanceService.getTotalCount(eventId);
            const deviceCount = await this.attendanceService.getDeviceCount(eventId);
            const deviceInfo = await this.deviceService.getDeviceInfo();

            console.log('🚨 AUDIT RESPONSE: Count data retrieved - Total:', totalCount, 'Device:', deviceCount);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 Count data retrieved - Total: ${totalCount}, Device: ${deviceCount}`);

            // Prepare audit response
            const auditResponse = {
                type: 'audit-response',
                eventId,
                deviceId: deviceInfo.deviceId,
                deviceName: deviceInfo.deviceName,
                platform: deviceInfo.platform,
                userDisplayName: deviceInfo.userDisplayName,
                totalCount: totalCount || 0,
                deviceCount: deviceCount ?? 0,
                timestamp: Date.now(),
                respondingTo: requesterId
            };

            console.log('🚨 AUDIT RESPONSE: Prepared audit response:', auditResponse);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT RESPONSE: Prepared audit response:`, auditResponse);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT RESPONSE: About to broadcast response to all peers`);

            // Send response back
            await this.broadcastDataMessage(auditResponse, 'system');
            console.log('🚨 AUDIT RESPONSE: ✅ Audit response broadcast completed for requester:', requesterId);
            this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT RESPONSE: ✅ Audit response sent successfully to requester: ${requesterId}`);
        } catch (error) {
            console.error('🚨 AUDIT RESPONSE: ❌ Failed to handle audit request:', error);
            this.debugLoggerService.error('WebRTC Mesh', `🔍 AUDIT RESPONSE: ❌ Failed to handle audit request:`, error);
        }
    }

    /**
     * Generate audit report with reconciliation
     */
    async generateAuditReport(eventId: string): Promise<{
        success: boolean;
        report?: {
            eventId: string;
            timestamp: number;
            requestingDevice: {
                deviceId: string;
                deviceName: string;
                totalCount: number;
                deviceCount: number;
            };
            peerDevices: Array<{
                deviceId: string;
                deviceName: string;
                platform: string;
                totalCount: number;
                deviceCount: number;
                userDisplayName?: string;
            }>;
            summary: {
                totalDevicesReporting: number;
                requestingDeviceTotalCount: number;
                medianTotalCount: number;
                highestTotalCount: number;
                lowestTotalCount: number;
                averageTotalCount: number;
                sumOfDeviceCounts: number;
                discrepancies: Array<{
                    deviceName: string;
                    issue: string;
                    value: number;
                }>;
                recommendation: string;
            };
        };
        error?: string;
    }> {
        try {
            this.debugLoggerService.log('WebRTC Mesh', `Generating audit report for event: ${eventId}`);

            // Get local device's own data  
            const deviceInfo = await this.deviceService.getDeviceInfo();
            const localTotalCount = await this.attendanceService.getTotalCount(eventId) || 0;
            const localDeviceCount = await this.attendanceService.getDeviceCount(eventId) || 0;

            // Request audit from all peers
            const auditResult = await this.requestAuditFromAllPeers(eventId);

            if (!auditResult.success) {
                return {
                    success: false,
                    error: auditResult.error
                };
            }

            const peerDevices = auditResult.auditResults || [];

            // Calculate summary statistics
            const allTotalCounts = [localTotalCount, ...peerDevices.map(d => d.totalCount)];
            const allDeviceCounts = [localDeviceCount, ...peerDevices.map(d => d.deviceCount)];

            const highestTotalCount = Math.max(...allTotalCounts);
            const lowestTotalCount = Math.min(...allTotalCounts);
            const averageTotalCount = Math.round(allTotalCounts.reduce((a, b) => a + b, 0) / allTotalCounts.length);
            const sumOfDeviceCounts = allDeviceCounts.reduce((a, b) => a + b, 0);

            // Detect discrepancies
            const discrepancies: Array<{ deviceName: string; issue: string; value: number }> = [];

            // Check if any device has a significantly different total count compared to the median
            const sortedCounts = [...allTotalCounts].sort((a, b) => a - b);
            const medianCount = sortedCounts[Math.floor(sortedCounts.length / 2)];
            const countThreshold = Math.max(1, Math.floor(highestTotalCount * 0.05)); // 5% threshold

            // Check local device against median
            if (Math.abs(localTotalCount - medianCount) > countThreshold) {
                discrepancies.push({
                    deviceName: deviceInfo.deviceName,
                    issue: `Total count differs from median by ${Math.abs(localTotalCount - medianCount)}`,
                    value: localTotalCount
                });
            }

            // Check peer devices against median  
            peerDevices.forEach(device => {
                if (Math.abs(device.totalCount - medianCount) > countThreshold) {
                    discrepancies.push({
                        deviceName: device.deviceName,
                        issue: `Total count differs from median by ${Math.abs(device.totalCount - medianCount)}`,
                        value: device.totalCount
                    });
                }
            });

            // Generate recommendation
            let recommendation = '';
            if (discrepancies.length === 0) {
                recommendation = `All devices report consistent counts. Recommended total: ${highestTotalCount}`;
            } else if (discrepancies.length <= peerDevices.length * 0.3) {
                recommendation = `Minor discrepancies detected. Recommended total: ${highestTotalCount} (majority consensus)`;
            } else {
                recommendation = `Significant discrepancies detected. Consider manual verification. Range: ${lowestTotalCount}-${highestTotalCount}`;
            }

            const report = {
                eventId,
                timestamp: Date.now(),
                requestingDevice: {
                    deviceId: deviceInfo.deviceId,
                    deviceName: deviceInfo.deviceName,
                    totalCount: localTotalCount,
                    deviceCount: localDeviceCount
                },
                peerDevices,
                summary: {
                    totalDevicesReporting: peerDevices.length + 1, // +1 for requesting device
                    requestingDeviceTotalCount: localTotalCount,
                    medianTotalCount: medianCount,
                    highestTotalCount,
                    lowestTotalCount,
                    averageTotalCount,
                    sumOfDeviceCounts,
                    discrepancies,
                    recommendation
                }
            };

            this.debugLoggerService.log('WebRTC Mesh', 'Audit report generated:', report);
            return {
                success: true,
                report
            };
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to generate audit report: ${error}`);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    public async handleSystemMessage(message: any): Promise<void> { // Changed type to any
        try {
            this.debugLoggerService.log('WebRTC Mesh', 'Received SYSTEM message data:', message);

            switch (message?.type) { // Use message?.type directly
                case 'audit-request':
                    console.log('🚨 AUDIT REQUEST: Received audit request for event', message.eventId, 'from', message.requesterId);
                    this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT REQUEST: Received audit request for event ${message.eventId} from ${message.requesterId}`);
                    this.debugLoggerService.log('WebRTC Mesh', `🔍 AUDIT REQUEST: Message details:`, message);
                    await this.handleAuditRequest(message); // Pass message directly
                    break;

                case 'count_sync':
                    if (message.attendeeCount !== undefined) { // Access message.attendeeCount directly
                        const event = this.eventService.getEventById(message.eventId);
                        if (event?.countMethod !== 'qr') {
                            const localCount_cs = this.attendanceService.count();
                            if (event?.countMethod === 'manual' && message.attendeeCount <= localCount_cs && !this.isDecreaseAllowed(message.eventId)) {
                                this.debugLoggerService.log('WebRTC Mesh', `Ignoring count_sync (${message.attendeeCount}) <= local (${localCount_cs}) for manual event`);
                            } else {
                                await this.attendanceService.processRemoteTotalCountUpdate(message.eventId, message.attendeeCount);
                                this.debugLoggerService.log('WebRTC Mesh', `Received count_sync from peer: ${message.senderId} for event ${message.eventId}. New total: ${message.attendeeCount}`);
                            }
                        }
                    } else {
                        this.debugLoggerService.warn('WebRTC Mesh', `Received count_sync message without attendeeCount:`, message);
                    }
                    break;

                case 'attendance_update':
                    await this.attendanceService.processRemoteAttendanceUpdate(message.ticketId, message.eventId, message.action, message.originDeviceId);
                    this.debugLoggerService.log('WebRTC Mesh', `Processed remote attendance update (${message.action}) from peer: ${message.senderId}`, { ticketId: message.ticketId, eventId: message.eventId });
                    break;

                case 'full_attendance_sync': // NEW case for full ticket list sync
                    this.debugLoggerService.log('WebRTC Mesh', 'Received full_attendance_sync message:', message);
                    if (message.tickets && Array.isArray(message.tickets)) {
                        this.debugLoggerService.log('WebRTC Mesh', `Receiving full attendance sync with ${message.tickets.length} tickets from peer: ${message.senderId}`);
                        await this.attendanceService.processTickets(message.tickets, true);
                        this.debugLoggerService.log('WebRTC Mesh', `Processed ${message.tickets.length} tickets from full attendance sync`);
                    } else {
                        this.debugLoggerService.warn('WebRTC Mesh', `Received full_attendance_sync message without a valid tickets array:`, message);
                    }
                    break;

                case 'manual_count_update':
                    // Consolidated message may include deviceCount/deviceId (preferred single-tap payload)
                    {
                        const senderDeviceId = message.deviceId || message.data?.deviceId || message.senderId;
                        const deviceCount = message.deviceCount ?? message.data?.deviceCount;
                        let appliedDeviceCount = false;
                        if (senderDeviceId && deviceCount !== undefined) {
                            let foundPeer = false;
                            for (const [, peer] of this.peers.entries()) {
                                if (peer.deviceId === senderDeviceId) {
                                    peer.deviceCount = deviceCount;
                                    this.debugLoggerService.log('WebRTC Mesh', `✅ Updated peer ${peer.deviceName || peer.deviceId} device count to ${deviceCount}`);
                                    foundPeer = true;
                                    break;
                                }
                            }
                            if (!foundPeer) {
                                this.debugLoggerService.log('WebRTC Mesh', `⚠️ Received device count in manual_count_update from unknown device: ${senderDeviceId}`);
                            }
                            this.updateConnectedPeers();
                            if (message.eventId) {
                                const reconciled = await this.reconcileManualTotal(message.eventId);
                                appliedDeviceCount = reconciled !== null;
                            }
                        }
                        if (!appliedDeviceCount) {
                            if (message.action === 'increment') {
                                await this.attendanceService.processRemoteManualCountUpdate(message.eventId, 'increment');
                                this.debugLoggerService.log('WebRTC Mesh', `Received manual increment from peer: ${message.senderId}`);
                            } else if (message.action === 'decrement') {
                                await this.attendanceService.processRemoteManualCountUpdate(message.eventId, 'decrement');
                                this.debugLoggerService.log('WebRTC Mesh', `Received manual decrement from peer: ${message.senderId}`);
                            }
                        }
                    }
                    // Debounced relay: batch rapid increments into a single count_sync
                    if (message.eventId) {
                        this._countSyncRelayEventId = message.eventId;
                        if (this._countSyncRelayTimer) clearTimeout(this._countSyncRelayTimer);
                        this._countSyncRelayTimer = setTimeout(() => {
                            const total = this.attendanceService.count();
                            this.broadcastDataMessage({
                                type: 'count_sync',
                                eventId: this._countSyncRelayEventId!,
                                attendeeCount: total,
                                timestamp: Date.now(),
                                senderId: this.signalingService.clientId || 'unknown'
                            }, 'system').catch(() => {});
                            this._countSyncRelayTimer = null;
                        }, 500);
                    }
                    break;

                case 'device_count_update':
                    // Legacy path: older clients may still send a separate device_count_update
                    {
                        const legacyDeviceId = message.deviceId || message.data?.deviceId || message.senderId || message.data?.senderId;
                        const legacyDeviceCount = message.deviceCount ?? message.data?.deviceCount;
                        if (legacyDeviceId && legacyDeviceCount !== undefined) {
                            let foundPeer = false;
                            for (const [, peer] of this.peers.entries()) {
                                if (peer.deviceId === legacyDeviceId) {
                                    peer.deviceCount = legacyDeviceCount;
                                    this.debugLoggerService.log('WebRTC Mesh', `✅ Updated peer ${peer.deviceName || peer.deviceId} device count to ${legacyDeviceCount}`);
                                    foundPeer = true;
                                    break;
                                }
                            }

                            if (!foundPeer) {
                                this.debugLoggerService.log('WebRTC Mesh', `⚠️ Received device count update from unknown device: ${legacyDeviceId}`);
                            }

                            this.updateConnectedPeers();
                        }
                    }
                    break;

                case 'audio_broadcaster_status':
                    this.debugLoggerService.log('WebRTC Mesh', `Audio broadcaster status update from ${message.senderId}: ${message.isActive ? 'started' : 'stopped'}`);
                    break;

                case 'voice_message_chunk': {
                    const messageId = message.messageId;
                    if (!messageId || !message.chunk || message.totalChunks === undefined) {
                        this.debugLoggerService.warn('WebRTC Mesh', 'Invalid voice message chunk received', message);
                        break;
                    }

                    const existing = this.voiceMessageChunks.get(messageId) || {
                        totalChunks: message.totalChunks,
                        chunks: [],
                        eventId: message.eventId,
                        mimeType: message.mimeType,
                        createdAt: message.createdAt || Date.now(),
                        sender: message.sender || {}
                    };

                    existing.totalChunks = message.totalChunks;
                    existing.chunks[message.chunkIndex] = message.chunk;
                    this.voiceMessageChunks.set(messageId, existing);

                    if (existing.chunks.filter(Boolean).length === existing.totalChunks) {
                        const base64Data = existing.chunks.join('');
                        const dataUrl = `data:${existing.mimeType};base64,${base64Data}`;
                        this._voiceMessageReceived.next({
                            eventId: existing.eventId,
                            messageId,
                            mimeType: existing.mimeType,
                            dataUrl,
                            createdAt: existing.createdAt,
                            sender: existing.sender
                        });
                        this.voiceMessageChunks.delete(messageId);
                    }
                    break;
                }

                case 'walky_talky_settings_sync': {
                    const syncedEnabled = !!message.enabled;
                    const syncedTarget: 'admins' | 'all' = message.target === 'all' ? 'all' : 'admins';
                    const syncedEventId = message.eventId || this.currentEventId;
                    let incomingTs =
                        typeof message.policyTimestamp === 'number' ? message.policyTimestamp : Date.now();
                    if (!Number.isFinite(incomingTs)) {
                        incomingTs = Date.now();
                    }

                    if (syncedEventId && this.currentEventId && syncedEventId !== this.currentEventId) {
                        this.debugLoggerService.log('WebRTC Mesh', 'Ignoring WalkyTalky policy for different event', {
                            policyEventId: syncedEventId,
                            currentEventId: this.currentEventId
                        });
                        break;
                    }

                    const lastTsRaw = await this.storage.get(this.WALKY_TALKY_POLICY_TS_KEY);
                    let lastTs =
                        lastTsRaw !== null && lastTsRaw !== undefined && String(lastTsRaw).length > 0
                            ? parseInt(String(lastTsRaw), 10)
                            : 0;
                    if (!Number.isFinite(lastTs)) {
                        lastTs = 0;
                    }
                    if (incomingTs < lastTs) {
                        this.debugLoggerService.log('WebRTC Mesh', 'Ignoring stale WalkyTalky policy', {
                            incomingTs,
                            lastTs
                        });
                        break;
                    }

                    await this.storage.set(this.WALKY_TALKY_ENABLED_KEY, syncedEnabled ? 'true' : 'false');
                    await this.storage.set(this.WALKY_TALKY_TARGET_KEY, syncedTarget);
                    await this.storage.set(this.WALKY_TALKY_POLICY_TS_KEY, String(incomingTs));

                    window.dispatchEvent(new CustomEvent(this.WALKY_TALKY_SETTINGS_EVENT, {
                        detail: {
                            enabled: syncedEnabled,
                            target: syncedTarget,
                            source: 'admin-sync',
                            eventId: syncedEventId,
                            policyTimestamp: incomingTs
                        }
                    }));

                    this.debugLoggerService.log('WebRTC Mesh', 'Applied WalkyTalky settings from mesh', {
                        enabled: syncedEnabled,
                        target: syncedTarget,
                        eventId: syncedEventId
                    });
                    break;
                }

                case 'count_state_sync':
                    if (message.tickets && message.tickets.length > 0) {
                        this.debugLoggerService.log('WebRTC Mesh', `Receiving count state sync with ${message.tickets.length} tickets from peer: ${message.senderId}`);
                        await this.attendanceService.processTickets(message.tickets, true); // The 'true' flag indicates a merge operation
                        this.debugLoggerService.log('WebRTC Mesh', `Processed ${message.tickets.length} tickets from count state sync`);
                    } else if (message.totalCount !== undefined) {

                        this.debugLoggerService.log('WebRTC Mesh', `Receiving manual count state sync from peer: ${message.senderId}`, {
                            totalCount: message.totalCount,
                            currentCount: this.attendanceService.count()
                        });

                        const event = this.eventService.getEventById(message.eventId);
                        if (event?.countMethod !== 'qr') {
                            const senderDeviceId = message.deviceId || message.data?.deviceId || message.deviceInfo?.deviceId;
                            const senderDeviceCount = message.deviceCount ?? message.data?.deviceCount;
                            if (senderDeviceId && senderDeviceCount !== undefined) {
                                for (const [, peer] of this.peers.entries()) {
                                    if (peer.deviceId === senderDeviceId) {
                                        peer.deviceCount = senderDeviceCount;
                                        break;
                                    }
                                }
                                this.updateConnectedPeers();
                            }

                            const reconciled = event?.countMethod === 'manual'
                                ? await this.reconcileManualTotal(message.eventId)
                                : null;
                            if (reconciled === null) {
                                const currentCount = this.attendanceService.count() || 0;
                                const effectiveTotal = Math.max(message.totalCount, currentCount);
                                await this.attendanceService.processRemoteTotalCountUpdate(message.eventId, effectiveTotal);
                                this.debugLoggerService.log('WebRTC Mesh', `Set total count to ${effectiveTotal} from count state sync (remote: ${message.totalCount})`);
                            }
                        }
                    }
                    break;

                case 'request-count-sync':
                    // Respond with current count state when requested (e.g., after foreground resume)
                    if (message.eventId === this.currentEventId && message.senderId) {
                        this.debugLoggerService.log('WebRTC Mesh', `Received count sync request from ${message.senderId}`);
                        await this.broadcastCurrentCountState(message.eventId, message.senderId);
                    }
                    break;

                case 'request-count-consensus':
                    // Respond with current count for consensus calculation
                    await this.handleCountConsensusRequest(message);
                    break;

                case 'count-consensus-response':
                    // Call the consensus handler if it exists for this request
                    if (message.requestId) {
                        const handlerKey = `_consensusHandler_${message.requestId}`;
                        const handler = (this as any)[handlerKey];
                        if (handler && typeof handler === 'function') {
                            handler(message);
                        }
                    }
                    break;

                case 'request-full-attendance-sync':
                case 'reconnection-check': // NEW: Handle reconnection-check as a request for full attendance sync
                    this.debugLoggerService.log('WebRTC Mesh', `Creator side received request-full-attendance-sync OR reconnection-check. isCreator: ${this._isCreator}, senderId: ${message.senderId}, eventId: ${message.eventId}`);
                    if (this._isCreator && (message.senderId || message.requesterId) && message.eventId) {
                        const eventId = message.eventId;
                        const currentEvent = this.eventService.getEventById(eventId);
                        this.debugLoggerService.log('WebRTC Mesh', `Inside request-full-attendance-sync/reconnection-check check. currentEvent: ${currentEvent?.id}, countMethod: ${currentEvent?.countMethod}`);
                        if (currentEvent && currentEvent.countMethod === 'qr') {
                            this.debugLoggerService.log('WebRTC Mesh', `Creator broadcasting full attendance update to ${message.senderId || message.requesterId} for event ${eventId} in response to request-full-attendance-sync/reconnection-check.`);
                            await this.broadcastAttendanceUpdate(eventId, message.senderId || message.requesterId);
                        } else {
                            this.debugLoggerService.log('WebRTC Mesh', `Creator skipping full attendance broadcast for event ${eventId}. Not a QR event or event not found.`);
                        }
                    } else {
                        this.debugLoggerService.warn('WebRTC Mesh', 'Ignoring request-full-attendance-sync/reconnection-check: not creator or missing eventId/senderId.', { isCreator: this._isCreator, senderId: message.senderId, eventId: message?.eventId });
                    }
                    break;

                case 'sync-request':
                    this.debugLoggerService.log('WebRTC Mesh', `Received sync-request from peer`, message);
                    await this.handleSyncRequest(message);
                    break;

                case 'sync-response':
                    this.debugLoggerService.log('WebRTC Mesh', `Received sync-response from peer`, message);
                    this.handleSyncResponse(message);
                    break;

                default:
                    this.debugLoggerService.log('WebRTC Mesh', `Unhandled system message type: ${message?.type}`); // Use message?.type directly
            }
        } catch (error: unknown) { // Explicitly type error as unknown
            this.debugLoggerService.error('WebRTC Mesh', `Error handling system message: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }



    /**
     * Manual recovery method - can be called from browser console
     * to force event data sharing when original creator deletes event
     */
    async forceEventDataRecovery(): Promise<void> {
        this.debugLoggerService.log('WebRTC Mesh', 'Starting manual event data recovery');

        try {
            // Get all events from localStorage
            const events: Array<{ key: string, data: any }> = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith('events_') && key !== 'events_events') {
                    try {
                        const data = localStorage.getItem(key);
                        if (data) {
                            const eventData = JSON.parse(data);
                            events.push({ key, data: eventData });
                        }
                    } catch (error) {
                        this.debugLoggerService.warn('WebRTC Mesh', `Failed to parse ${key}: ${error}`);
                    }
                }
            }

            this.debugLoggerService.log('WebRTC Mesh', `Found events in storage: ${events.length}`);

            // Find the most recent event
            let mostRecentEvent: any = null;
            let mostRecentTime = 0;

            events.forEach(({ key, data }) => {
                const timestamp = data.date ? new Date(data.date).getTime() : 0;
                if (timestamp > mostRecentTime) {
                    mostRecentTime = timestamp;
                    mostRecentEvent = data;
                }
            });

            if (mostRecentEvent) {
                this.debugLoggerService.log('WebRTC Mesh', `Found most recent event: ${mostRecentEvent.name}`);

                // Broadcast this event data to all connected peers
                const recoveryData = {
                    type: 'event_data_recovery',
                    event: mostRecentEvent,
                    timestamp: Date.now(),
                    senderId: 'recovery_system',
                    message: 'Event data recovery - please accept this event'
                };

                await this.broadcastDataMessage(recoveryData, 'system');
                this.debugLoggerService.log('WebRTC Mesh', 'Event recovery data broadcasted to all peers');

                return;
            } else {
                this.debugLoggerService.log('WebRTC Mesh', 'No events found in storage');
            }

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Event recovery failed: ${error}`);
        }
    }

    // Health monitoring methods
    private startHealthMonitoring(): void {
        this.healthCheckTimer = setInterval(() => this.performHealthCheck(), this.PEER_HEALTH_CHECK_INTERVAL);
        this.heartbeatTimer = setInterval(() => this.performHeartbeat(), this.HEARTBEAT_INTERVAL);
        this.debugLoggerService.log('WebRTC Mesh', 'Started health monitoring and heartbeat timers');

    }

    private stopHealthMonitoring(): void {
        clearInterval(this.healthCheckTimer);
        clearInterval(this.heartbeatTimer);
        this.debugLoggerService.log('WebRTC Mesh', 'Stopped health monitoring and heartbeat timers');
    }

    private async performHealthCheck(): Promise<void> {
        const now = Date.now();
        const orphanedPeers: string[] = [];

        this.debugLoggerService.log('WebRTC Mesh', 'Performing peer health check');

        // Update peer background status based on recent activity
        this.updatePeerBackgroundStatus();

        for (const [peerId, peer] of this.peers.entries()) {
            const lastSeen = this.lastSeenMap.get(peerId) || peer.lastSeen || now;
            const timeSinceLastSeen = now - lastSeen;

            const isRtcConnected = peer.connection.connectionState === 'connected' && peer.isConnected;
            const isDataChannelOpen = peer.dataChannel?.readyState === 'open';
            const isTimedOut = timeSinceLastSeen > this.PEER_TIMEOUT;

            if (!isRtcConnected) {
                this.debugLoggerService.warn('WebRTC Mesh', `Orphaned peer detected: ${peerId}`, {
                    deviceName: peer.deviceName,
                    connectionState: peer.connection.connectionState,
                    isConnected: peer.isConnected,
                    dataChannelState: peer.dataChannel?.readyState,
                    timeSinceLastSeen: Math.round(timeSinceLastSeen / 1000) + 's',
                    isTimedOut
                });
                orphanedPeers.push(peerId);
            } else if (!isDataChannelOpen) {
                // RTP connection is still alive (audio/video can flow) but data channel is dead.
                // Keep the peer alive for media; refresh lastSeen so the timeout doesn't accumulate.
                this.lastSeenMap.set(peerId, now);
                this.debugLoggerService.log('WebRTC Mesh', `Peer ${peerId} data channel closed but RTP connection alive - keeping for media`);
            } else if (isTimedOut) {
                this.debugLoggerService.warn('WebRTC Mesh', `Peer timed out: ${peerId}`, {
                    deviceName: peer.deviceName,
                    timeSinceLastSeen: Math.round(timeSinceLastSeen / 1000) + 's'
                });
                orphanedPeers.push(peerId);
            } else {
                // Send heartbeat ping to verify connection is still alive
                try {
                    this.sendHeartbeatPing(peer);
                } catch (error) {
                    this.debugLoggerService.warn('WebRTC Mesh', `Failed to send heartbeat to ${peerId}: ${error}`);
                    orphanedPeers.push(peerId);
                }
            }
        }

        // Clean up orphaned peers
        if (orphanedPeers.length > 0) {
            this.debugLoggerService.log('WebRTC Mesh', `Cleaning up ${orphanedPeers.length} orphaned peers:`, orphanedPeers);

            // Emergency UI cleanup before peer cleanup to prevent UI blocking
            this.performEmergencyUICleanup();

            for (const peerId of orphanedPeers) {
                this.cleanupOrphanedPeer(peerId);
            }

            // Force update connected peers list
            this.updateConnectedPeers();

            // Additional UI cleanup after peer cleanup
            setTimeout(() => {
                this.performEmergencyUICleanup();
            }, 1000);

            // Emit event for components to react to cleanup
            this._eventsSignal.set({
                type: 'peers-cleaned',
                peerId: 'system',
                data: {
                    cleanedPeers: orphanedPeers,
                    remainingPeers: this.getConnectedPeers().length
                }
            });
        }

        this.debugLoggerService.log('WebRTC Mesh', `Health check completed. Active peers: ${this.getConnectedPeers().length}`);
    }

    private async performHeartbeat(): Promise<void> {
        const connectedPeers = this.getConnectedPeers();

        for (const peer of connectedPeers) {
            // Update last seen for active peers
            const now = Date.now();
            this.lastSeenMap.set(peer.id, now);

            // Update peer's lastSeen property
            peer.lastSeen = now;
        }
        this.debugLoggerService.log('WebRTC Mesh', 'Performed heartbeat for connected peers');

    }

    private sendHeartbeatPing(peer: WebRTCPeer): void {
        if (peer.dataChannel && peer.dataChannel.readyState === 'open') {
            if (!this.canSendOnDataChannel(peer.dataChannel, 'heartbeat-ping')) {
                this.debugLoggerService.warn('WebRTC Mesh', `Skipping heartbeat to ${peer.id}: data channel backpressure`);
                return;
            }
            const pingMessage: ChannelMessage = {
                type: 'heartbeat-ping',
                data: { timestamp: Date.now() },
                timestamp: Date.now(),
                senderId: this.signalingService.clientId || 'unknown'
            };

            peer.dataChannel.send(JSON.stringify(pingMessage));
            this.debugLoggerService.log('WebRTC Mesh', `Sent heartbeat ping to ${peer.id}`);
        } else {
            throw new Error(`Data channel not available for peer ${peer.id}`);
        }
    }

    private cleanupOrphanedPeer(peerId: string): void {
        const peer = this.peers.get(peerId);
        if (peer) {
            this.debugLoggerService.log('WebRTC Mesh', `Cleaning up orphaned peer: ${peerId} (${peer.deviceName})`);

            try {
                // Close all channels
                if (peer.dataChannel) {
                    peer.dataChannel.close();
                }
                // Removed audioChannel and videoChannel as they are no longer data channels

                // Close peer connection
                peer.connection.close();
            } catch (error) {
                this.debugLoggerService.warn('WebRTC Mesh', `Error closing connection for peer ${peerId}: ${error}`);
            }

            // Remove from maps
            this.peers.delete(peerId);
            this.lastSeenMap.delete(peerId);
            this.peerRttMap.delete(peerId);

            // Emit disconnection event
            this._eventsSignal.set({
                type: 'peer-disconnected',
                peerId
            });
            this.debugLoggerService.log('WebRTC Mesh', `Cleaned up peer ${peerId}`);
        }
    }

    private getOrphanedPeers(): Array<{ peerId: string, deviceName: string, lastSeen: number, issue: string }> {
        const now = Date.now();
        const orphaned: Array<{ peerId: string, deviceName: string, lastSeen: number, issue: string }> = [];

        for (const [peerId, peer] of this.peers.entries()) {
            const lastSeen = this.lastSeenMap.get(peerId) || peer.lastSeen || now;
            const timeSinceLastSeen = now - lastSeen;

            let issue = '';
            if (peer.connection.connectionState !== 'connected') {
                issue = `Connection state: ${peer.connection.connectionState}`;
            } else if (!peer.isConnected) {
                issue = 'Marked as disconnected';
            } else if (peer.dataChannel?.readyState !== 'open') {
                issue = `Data channel state: ${peer.dataChannel?.readyState || 'none'}`;
            } else if (timeSinceLastSeen > this.PEER_TIMEOUT) {
                issue = `Timeout: ${Math.round(timeSinceLastSeen / 1000)}s since last seen`;
            }

            if (issue) {
                orphaned.push({
                    peerId,
                    deviceName: peer.deviceName || 'Unknown',
                    lastSeen,
                    issue
                });
            }
        }

        return orphaned;
    }

    private performEmergencyUICleanup(): void {
        this.debugLoggerService.log('WebRTC Mesh', 'Performing emergency UI cleanup during WebRTC peer cleanup');

        try {
            // Remove any stale backdrops that might be blocking interactions
            const staleBackdrops = document.querySelectorAll('ion-backdrop[aria-hidden="true"], ion-backdrop:not([aria-hidden])');
            if (staleBackdrops.length > 0) {
                this.debugLoggerService.log('WebRTC Mesh', `Removing ${staleBackdrops.length} stale backdrops`);
                staleBackdrops.forEach(backdrop => backdrop.remove());
            }

            // Remove any orphaned modals or alerts
            const orphanedModals = document.querySelectorAll('ion-modal:not(.show-modal), ion-alert:not(.alert-visible)');
            if (orphanedModals.length > 0) {
                this.debugLoggerService.log('WebRTC Mesh', `Removing ${orphanedModals.length} orphaned modals/alerts`);
                orphanedModals.forEach(modal => modal.remove());
            }

            // Reset pointer events on critical navigation elements
            const tabButtons = document.querySelectorAll('ion-tab-button');
            tabButtons.forEach(button => {
                (button as HTMLElement).style.pointerEvents = 'auto';
                (button as HTMLElement).style.cursor = 'pointer';
            });

            const tabBars = document.querySelectorAll('ion-tab-bar');
            tabBars.forEach(tabBar => {
                (tabBar as HTMLElement).style.pointerEvents = 'auto';
                (tabBar as HTMLElement).style.zIndex = '1000';
            });

            // Ensure body is clickable
            document.body.style.pointerEvents = 'auto';
            document.body.style.userSelect = 'auto';
            document.body.style.touchAction = 'auto';

            // Reset app container
            const ionApp = document.querySelector('ion-app');
            if (ionApp) {
                (ionApp as HTMLElement).style.pointerEvents = 'auto';
                (ionApp as HTMLElement).style.overflow = 'auto';
            }

            // Force focus back to body if something else has captured it
            if (document.activeElement && document.activeElement !== document.body) {
                (document.activeElement as HTMLElement).blur();
            }
            document.body.focus();

            this.debugLoggerService.log('WebRTC Mesh', 'Emergency UI cleanup completed');

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Emergency UI cleanup failed: ${error}`);
        }
    }

    /**
     * Manual cleanup of stale peer connections
     * Useful when audit system detects communication issues
     */
    public cleanupStalePeers(): number {
        const allPeers = Array.from(this.peers.values());
        const stalePeers: string[] = [];

        for (const peer of allPeers) {
            const isStale = peer.connection?.connectionState === 'failed' ||
                peer.connection?.connectionState === 'disconnected' ||
                peer.connection?.connectionState === 'closed' ||
                peer.dataChannel?.readyState === 'closed' ||
                (peer.connection?.connectionState === 'connecting' && this.isConnectionStale(peer));

            if (isStale) {
                stalePeers.push(peer.id);
            }
        }

        if (stalePeers.length > 0) {
            this.debugLoggerService.log('WebRTC Mesh', `Manual cleanup: removing ${stalePeers.length} stale peers`);

            for (const peerId of stalePeers) {
                this.cleanupOrphanedPeer(peerId);
            }

            // Update connected peers list
            this.updateConnectedPeers();

            // Emit cleanup event
            this._eventsSignal.set({
                type: 'peers-cleaned',
                peerId: 'system',
                data: {
                    cleanedPeers: stalePeers,
                    remainingPeers: this.getConnectedPeers().length,
                    reason: 'manual-stale-cleanup'
                }
            });
        }

        return stalePeers.length;
    }

    private canSendOnDataChannel(channel: RTCDataChannel, messageType?: string): boolean {
        if (channel.readyState !== 'open') {
            return false;
        }
        if (channel.bufferedAmount <= this.DATA_CHANNEL_BUFFERED_AMOUNT_LIMIT) {
            return true;
        }
        // Prefer dropping count snapshots over blocking the channel under congestion
        const droppableTypes = new Set(['system', 'device-info', 'count_sync', 'peer_count_update', 'heartbeat-ping']);
        if (messageType && droppableTypes.has(messageType)) {
            return false;
        }
        return channel.bufferedAmount < this.DATA_CHANNEL_BUFFERED_AMOUNT_LIMIT * 2;
    }

    private shouldUseLeanMeshMode(): boolean {
        if (this.peerRttMap.size === 0) {
            return false;
        }
        let maxRtt = 0;
        for (const rtt of this.peerRttMap.values()) {
            if (rtt > maxRtt) {
                maxRtt = rtt;
            }
        }
        return maxRtt >= this.LEAN_MODE_RTT_MS;
    }

    private async createPeer(peerId: string, isInitiator: boolean): Promise<WebRTCPeer> {
        const connection = new RTCPeerConnection({
            iceServers: this.config.iceServers
        });

        const now = Date.now();
        const peer: WebRTCPeer = {
            id: peerId,
            connection,
            isConnected: false,
            isInitiator,
            lastSeen: now
        };

        this.peers.set(peerId, peer);
        this.lastSeenMap.set(peerId, now);

        // Setup connection event handlers
        this.setupPeerConnectionHandlers(peer);

        // Add local stream to connection
        if (this.localStream) {
            this.localStream.getTracks().forEach(track => {
                connection.addTrack(track, this.localStream!);
            }
            );
        }

        // Create data channels if initiator
        if (isInitiator) {
            await this.createDataChannels(peer);
        }
        this.debugLoggerService.log('WebRTC Mesh', `Created peer ${peer.id}, initiator: ${isInitiator}`);

        return peer;
    }

    private async createDataChannels(peer: WebRTCPeer): Promise<void> {
        // Always create the primary data channel for text/file communication and counter sync
        peer.dataChannel = peer.connection.createDataChannel(ChannelType.DATA, {
            ordered: true
        });
        this.setupDataChannelHandlers(peer, peer.dataChannel, ChannelType.DATA);

        this.debugLoggerService.log('WebRTC Mesh', `Primary data channel created for peer ${peer.id} - ready for counter and event synchronization`);

        // Media streams (audio/video) are handled directly via addTrack/ontrack, not data channels.
        // The data channels for audio/video have been removed from webrtc.models.ts.
        this.debugLoggerService.log('WebRTC Mesh', `Media streams will be handled by RTCPeerConnection.addTrack/ontrack for peer ${peer.id}. Data channels for audio/video are deprecated.`);
    }

    private async setupPeerConnectionHandlers(peer: WebRTCPeer): Promise<void> {
        const { connection } = peer;

        connection.onicecandidate = (event) => {
            if (event.candidate) {
                this.signalingService.sendIceCandidate(peer.id, event.candidate.toJSON());
                this.debugLoggerService.log('WebRTC Mesh', `Sent ICE candidate for peer ${peer.id}`);
            }
        };

        connection.onconnectionstatechange = () => {
            this.debugLoggerService.log('WebRTC Mesh', `Peer ${peer.id} connection state: ${connection.connectionState}`);

            if (connection.connectionState === 'connected') {
                peer.isConnected = true;
                this.updateConnectedPeers();
                this.debugLoggerService.log('WebRTC Mesh', `Peer ${peer.id} is now connected`);

                // Broadcast device info to the newly connected peer
                this.broadcastDeviceInfo().catch(error => {
                    this.debugLoggerService.warn('WebRTC Mesh', `Failed to broadcast device info: ${error}`);
                });

                // Get current event for sync operations
                const currentEvent = this.eventService.getCurrentEvent();
                const eventId = currentEvent?.id;
                this.debugLoggerService.log('WebRTC Mesh', `Broadcasting count state to new peer: ${eventId} ${peer.id}`);
                this.debugLoggerService.log('WebRTC Mesh', `Peer ${peer.id} data channel state: ${peer.dataChannel?.readyState}`);

                // If this is a reconnection (peer was previously connected), trigger consensus sync for manual events
                // This handles the case where this device lost WiFi, counted offline, and is now reconnecting
                if (currentEvent && currentEvent.countMethod === 'manual' && this.wasDevicePreviouslyConnected(peer.id)) {
                    this.debugLoggerService.log('WebRTC Mesh', `Peer ${peer.id} reconnected - triggering consensus sync for manual event`);
                    // Small delay to ensure data channel is fully ready
                    const eventIdForSync = currentEvent.id; // Capture the event ID
                    setTimeout(async () => {
                        try {
                            await this.requestConsensusCountSync(eventIdForSync);
                        } catch (error) {
                            this.debugLoggerService.error('WebRTC Mesh', 'Error during reconnection consensus sync:', error);
                        }
                    }, 1000);
                }

                // Send current count state to the new peer
                if (eventId) {
                    this.broadcastCurrentCountState(eventId, peer.id).catch(error => {
                        this.debugLoggerService.warn('WebRTC Mesh', `Failed to broadcast count state: ${error}`);
                    });

                    // Also broadcast attendance update (legacy)
                    this.broadcastAttendanceUpdate(eventId, peer.id).catch(error => {
                        this.debugLoggerService.warn('WebRTC Mesh', `Failed to broadcast attendance update: ${error}`);
                    });
                } else {
                    this.debugLoggerService.warn('WebRTC Mesh', 'No current event available to broadcast count state');
                }

                // Trigger callback for automatic data sharing
                if (this.onPeerConnectedCallback) {

                    try {
                        this.onPeerConnectedCallback(peer.id);
                    } catch (error) {
                        this.debugLoggerService.warn('WebRTC Mesh', `Error in peer connected callback: ${error}`);
                    }
                }

                this._eventsSignal.set({
                    type: 'peer-connected',
                    peerId: peer.id
                });
            } else if (connection.connectionState === 'disconnected') {
                this.debugLoggerService.log('WebRTC Mesh', `Peer ${peer.id} connection temporarily disconnected - attempting ICE restart`);
                this.attemptIceRestart(peer);
            } else if (connection.connectionState === 'failed') {
                peer.isConnected = false;
                this.updateConnectedPeers();
                this.debugLoggerService.log('WebRTC Mesh', `Peer ${peer.id} connection failed`);
                this._eventsSignal.set({
                    type: 'peer-disconnected',
                    peerId: peer.id
                });
            } else if (connection.connectionState === 'closed') {
                // Perform cleanup here if necessary
                this.disconnectFromPeer(peer.id); // Ensure peer is fully removed
            }
        };

        connection.ontrack = (event) => {
            this.debugLoggerService.log('WebRTC Mesh', `Received track from peer ${peer.id}`);
            peer.remoteStream = event.streams[0];
            this._eventsSignal.set({
                type: 'stream-received',
                peerId: peer.id,
                stream: event.streams[0]
            });
        };

        connection.ondatachannel = (event) => {
            const channel = event.channel;
            this.debugLoggerService.log('WebRTC Mesh', `Received data channel: ${channel.label}`);

            switch (channel.label) {
                case ChannelType.DATA:
                    peer.dataChannel = channel;
                    break;
                // Removed audio and video data channel cases as media streams are handled by addTrack/ontrack
            }

            this.setupDataChannelHandlers(peer, channel, channel.label as ChannelType);
        };
    }

    private setupDataChannelHandlers(peer: WebRTCPeer, channel: RTCDataChannel, type: ChannelType): void {
        channel.onopen = () => {
            this.debugLoggerService.log('WebRTC Mesh', `${type} channel opened with peer ${peer.id}`);

            // Immediately broadcast device info when data channel opens
            if (type === ChannelType.DATA) {
                this.broadcastDeviceInfo().catch(error => {
                    this.debugLoggerService.warn('WebRTC Mesh', `Failed to broadcast device info on channel open: ${error}`);
                });

                // Send initial count state sync to the new peer (if this device is the authoritative source)
                // Only do this if we have a current event and this peer is not the local peer
                const currentEvent = this.eventService.getCurrentEvent();
                if (currentEvent && peer.id !== this.signalingService.clientId) {
                    // Optionally, you can check for isCreator or mesh host logic here
                    this.broadcastCurrentCountState(currentEvent.id, peer.id).catch(error => {
                        this.debugLoggerService.warn('WebRTC Mesh', `Failed to send initial count state sync on channel open: ${error}`);
                    });

                    // WalkyTalky policy: any mesh participant sends local policy so new peers converge without waiting for the original creator device.
                    this.sendWalkyTalkyPolicyToPeer(peer.id, currentEvent.id).catch(error => {
                        this.debugLoggerService.warn('WebRTC Mesh', `Failed to send WalkyTalky policy on peer join: ${error}`);
                    });
                }
            }
        };

        channel.onclose = () => {
            this.debugLoggerService.log('WebRTC Mesh', `${type} channel closed with peer ${peer.id}`);
        };

        channel.onerror = (error) => {
            this.debugLoggerService.error('WebRTC Mesh', `${type} channel error with peer ${peer.id}: ${error}`);
        };

        channel.onmessage = async (event) => {
            try {
                const message: ChannelMessage = JSON.parse(event.data);
                this.debugLoggerService.log('WebRTC Mesh', `Received ${type} message from ${peer.id}:`, message);

                // Update last seen timestamp for any received message
                const now = Date.now();
                this.lastSeenMap.set(peer.id, now);
                peer.lastSeen = now;
                // Handle heartbeat messages
                if (message.type === 'heartbeat-ping') {
                    // Respond to heartbeat ping
                    const pongMessage: ChannelMessage = {
                        type: 'heartbeat-pong',
                        data: {
                            originalTimestamp: message.data.timestamp,
                            responseTimestamp: now
                        },
                        timestamp: now,
                        senderId: this.signalingService.clientId || 'unknown'
                    };

                    if (peer.dataChannel && peer.dataChannel.readyState === 'open') {
                        peer.dataChannel.send(JSON.stringify(pongMessage));
                    }

                    this.debugLoggerService.log('WebRTC Mesh', `Responded to heartbeat ping from ${peer.id}`);
                    return; // Don't process heartbeat pings further
                }

                if (message.type === 'heartbeat-pong') {
                    const rtt = now - message.data.originalTimestamp;
                    this.peerRttMap.set(peer.id, rtt);
                    this.debugLoggerService.log('WebRTC Mesh', `Received heartbeat pong from ${peer.id}, roundtrip: ${rtt}ms`);
                    return; // Don't process heartbeat pongs further
                }

                // Handle background ping messages
                if (message.type === 'background-ping') {
                    this.handleBackgroundPing(message, peer.id);
                    return; // Don't process background pings further
                }

                // Handle device information messages
                if (message.type === 'device-info') {
                    this.debugLoggerService.log('WebRTC Mesh', `Processing device-info message from ${peer.id}:`, message.data);
                    this.updatePeerDeviceInfo(peer, message);
                }

                // Update peer device info from message headers if available
                if (message.senderDeviceId || message.senderDeviceName) {
                    this.debugLoggerService.log('WebRTC Mesh', `Updating peer ${peer.id} device info from message headers:`, {
                        senderDeviceId: message.senderDeviceId,
                        senderDeviceName: message.senderDeviceName,
                        senderPlatform: message.senderPlatform
                    });

                    peer.deviceId = message.senderDeviceId || peer.deviceId;
                    peer.deviceName = message.senderDeviceName || peer.deviceName;
                    peer.platform = message.senderPlatform || peer.platform;
                    peer.lastSeen = now;

                    // Update user identification if available
                    peer.userPhoneNumber = message.senderPhoneNumber || peer.userPhoneNumber;
                    peer.userName = message.senderUserName || peer.userName;
                    peer.userDisplayName = message.senderDisplayName || peer.userDisplayName;
                    peer.abbreviatedName = message.senderAbbreviatedName || peer.abbreviatedName;

                    // Update event counts if available
                    if (message.attendeeCount !== undefined) {
                        peer.attendeeCount = message.attendeeCount;
                    }
                    if (message.eventCount !== undefined) {
                        peer.eventCount = message.eventCount;
                    }
                    // NEW: Accept deviceCount from device-info messages for audit purposes
                    if (message.data?.deviceCount !== undefined) {
                        peer.deviceCount = message.data.deviceCount;
                        this.debugLoggerService.log('WebRTC Mesh', `Updated peer ${peer.id} deviceCount to ${peer.deviceCount} from device-info message`);
                        if (this.currentEventId) {
                            void this.reconcileManualTotal(this.currentEventId);
                        }
                    }

                    this.debugLoggerService.log('WebRTC Mesh', `Updated enhanced device info for peer ${peer.id}:`, {
                        deviceName: peer.deviceName,
                        platform: peer.platform,
                        userDisplayName: peer.userDisplayName,
                        abbreviatedName: peer.abbreviatedName,
                        attendeeCount: peer.attendeeCount
                    });

                    // Also update the WebSocket signaling service's peer info
                    this.updateSignalingServicePeerInfo(peer);

                    this.updateConnectedPeers();

                    // NEW: Discrepancy check for QR events
                    if (this.currentEventId) {
                        const currentEvent = this.eventService.getEventById(this.currentEventId);
                        if (currentEvent && currentEvent.countMethod === 'qr' && message.data.attendeeCount !== undefined) {
                            const localTotalCount = await this.attendanceService.getTotalScanned(this.currentEventId);
                            if (message.data.attendeeCount > localTotalCount) {
                                this.debugLoggerService.log('WebRTC Mesh', `Discrepancy detected for event ${this.currentEventId}. Local: ${localTotalCount}, Remote: ${message.data.attendeeCount}. Requesting full sync from ${message.senderId}`);
                                await this.requestFullAttendanceSync(this.currentEventId, message.senderId);
                            } else {
                                this.debugLoggerService.log('WebRTC Mesh', `No discrepancy or remote count not higher for event ${this.currentEventId}. Local: ${localTotalCount}, Remote: ${message.data.attendeeCount}.`);
                            }
                        } else if (currentEvent) {
                            this.debugLoggerService.log('WebRTC Mesh', `Discrepancy check skipped: currentEvent is not 'qr' (${currentEvent.countMethod}) or attendeeCount is undefined.`);
                        } else {
                            this.debugLoggerService.log('WebRTC Mesh', 'Discrepancy check skipped: currentEvent is null.');
                        }
                    }
                }

                // Cache important messages for late-joining subscribers
                this.cacheMessage(message);

                this._dataMessagesSignal.set(message);

                // NEW: Call audit response handler if it's active
                if (this.auditResponseHandler) {
                    this.auditResponseHandler(message);
                }

                this._eventsSignal.set({
                    type: 'data-received',
                    peerId: peer.id,
                    data: message
                });

                // Handle special system messages
                if (message.type === 'system') {
                    await this.handleSystemMessage(message.data); // Pass message.data instead of message
                }
            } catch (error) {
                this.debugLoggerService.error('WebRTC Mesh', `Error parsing ${type} message from ${peer.id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        };
    }

    private iceRestartInProgress = new Set<string>();

    private async attemptIceRestart(peer: WebRTCPeer): Promise<void> {
        if (this.iceRestartInProgress.has(peer.id)) return;
        if (!peer.isInitiator) {
            this.debugLoggerService.log('WebRTC Mesh', `Skipping ICE restart for ${peer.id} - not initiator, waiting for remote restart`);
            return;
        }
        this.iceRestartInProgress.add(peer.id);
        try {
            if (peer.connection.signalingState !== 'stable') {
                this.debugLoggerService.warn('WebRTC Mesh', `Cannot ICE restart for ${peer.id} in signaling state: ${peer.connection.signalingState}`);
                return;
            }
            const offer = await peer.connection.createOffer({ iceRestart: true });
            await peer.connection.setLocalDescription(offer);
            this.signalingService.sendOffer(peer.id, offer);
            this.debugLoggerService.log('WebRTC Mesh', `ICE restart offer sent for peer ${peer.id}`);
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `ICE restart failed for peer ${peer.id}: ${error}`);
        } finally {
            this.iceRestartInProgress.delete(peer.id);
        }
    }

    private async createOffer(peer: WebRTCPeer): Promise<void> {
        try {
            this.debugLoggerService.log('WebRTC Mesh', `Creating offer for peer ${peer.id}, signaling state: ${peer.connection.signalingState}`);

            if (peer.connection.signalingState !== 'stable' && peer.connection.signalingState !== 'have-local-offer') {
                this.debugLoggerService.warn('WebRTC Mesh', `Cannot create offer for peer ${peer.id} in signaling state: ${peer.connection.signalingState}`);
                return;
            }

            const offer = await peer.connection.createOffer();
            await peer.connection.setLocalDescription(offer);
            this.signalingService.sendOffer(peer.id, offer);
            this.debugLoggerService.log('WebRTC Mesh', `Offer created and sent for peer ${peer.id}`);
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to create offer for peer ${peer.id}: ${error}`);
            throw error;
        }
    }

    private async handleOffer(fromId: string, offer: RTCSessionDescriptionInit): Promise<void> {
        try {
            let peer = this.peers.get(fromId);
            if (!peer) {
                peer = await this.createPeer(fromId, false);
            }

            await peer.connection.setRemoteDescription(offer);
            const answer = await peer.connection.createAnswer();
            await peer.connection.setLocalDescription(answer);

            this.signalingService.sendAnswer(fromId, answer);
            this.debugLoggerService.log('WebRTC Mesh', `Handled offer from ${fromId}`);

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to handle offer from ${fromId}: ${error}`);
        }
    }

    private async handleAnswer(fromId: string, answer: RTCSessionDescriptionInit): Promise<void> {
        try {
            const peer = this.peers.get(fromId);
            if (peer) {
                await peer.connection.setRemoteDescription(answer);
                this.debugLoggerService.log('WebRTC Mesh', `Handled answer from ${fromId}`);

            }
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to handle answer from ${fromId}: ${error}`);
        }
    }

    private async handleIceCandidate(fromId: string, candidate: RTCIceCandidateInit): Promise<void> {
        try {
            const peer = this.peers.get(fromId);
            if (peer) {
                await peer.connection.addIceCandidate(new RTCIceCandidate(candidate));
                this.debugLoggerService.log('WebRTC Mesh', `Handled ICE candidate from ${fromId}`);
            }
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to handle ICE candidate from ${fromId}: ${error}`);
        }
    }

    private updateConnectedPeers(): void {
        const previousPeerCount = this._connectedPeersSignal().length;
        const currentConnectedPeers = this.getConnectedPeers();
        const currentPeerCount = currentConnectedPeers.length;

        this._connectedPeersSignal.set(currentConnectedPeers);
        this.debugLoggerService.log('WebRTC Mesh', 'Connected peers updated', currentConnectedPeers.map(p => p.id));

        // NEW: Handle device reconnection synchronization
        if (previousPeerCount === 0 && currentPeerCount > 0 && this.currentEventId) {
            // Device just connected to peers - this might be a reconnection
            this.debugLoggerService.log('WebRTC Mesh', 'First peer connection detected - checking for reconnection sync');
            // Delay the sync check to allow connections to stabilize
            setTimeout(() => {
                if (this.currentEventId) {
                    this.handleEventReconnection(this.currentEventId).catch(error => {
                        this.debugLoggerService.error('WebRTC Mesh', 'Error during reconnection sync:', error);
                    });
                }
            }, 3000); // 3 second delay
        }
    }

    /**
     * Handle background ping messages
     */
    private handleBackgroundPing(message: ChannelMessage, peerId: string): void {
        const peer = this.peers.get(peerId);
        if (peer) {
            peer.isInBackground = true;
            peer.lastBackgroundPing = Date.now();
            peer.backgroundPingCount = (peer.backgroundPingCount || 0) + 1;
            peer.lastSeen = Date.now();

            this.debugLoggerService.log('WebRTC Mesh', `📱 Background ping received from ${peer.deviceName || peerId}`);

            // Update signaling service with background tracking info
            this.updateSignalingServicePeerInfo(peer);

            // Update connected peers signal to trigger UI update
            this.updateConnectedPeers();
        }
    }

    /**
     * Update peer background status based on recent activity
     */
    private updatePeerBackgroundStatus(): void {
        const now = Date.now();
        const BACKGROUND_TIMEOUT = 60000; // 1 minute without activity = background

        for (const [peerId, peer] of this.peers) {
            const timeSinceLastSeen = now - (peer.lastSeen || 0);
            const wasInBackground = peer.isInBackground;

            // If peer was recently active but not connected, mark as background
            if (!peer.isConnected && timeSinceLastSeen < BACKGROUND_TIMEOUT && peer.lastSeen && peer.lastSeen > 0) {
                if (!wasInBackground) {
                    peer.isInBackground = true;
                    this.debugLoggerService.log('WebRTC Mesh', `📱 Marking peer ${peer.deviceName || peerId} as background due to inactivity`);
                    this.updateSignalingServicePeerInfo(peer);
                }
            }
            // If peer has been inactive for too long, mark as disconnected
            else if (timeSinceLastSeen > BACKGROUND_TIMEOUT && peer.lastSeen && peer.lastSeen > 0) {
                if (wasInBackground) {
                    peer.isInBackground = false;
                    this.debugLoggerService.log('WebRTC Mesh', `📱 Marking peer ${peer.deviceName || peerId} as disconnected due to timeout`);
                    this.updateSignalingServicePeerInfo(peer);
                }
            }
        }

        this.updateConnectedPeers();
    }

    private async updatePeerDeviceInfo(peer: WebRTCPeer, message: ChannelMessage): Promise<void> {
        if (message.data && typeof message.data === 'object') {
            this.debugLoggerService.log('WebRTC Mesh', 'Entering updatePeerDeviceInfo', { currentEventId: this.currentEventId, receivedAttendeeCount: message.data.attendeeCount });

            // Update device information
            peer.deviceId = message.data.deviceId || peer.deviceId;
            peer.deviceName = message.data.deviceName || peer.deviceName;
            peer.platform = message.data.platform || peer.platform;
            peer.lastSeen = Date.now();

            // Update user identification if available
            peer.userPhoneNumber = message.data.userPhoneNumber || message.senderPhoneNumber || peer.userPhoneNumber;
            peer.userName = message.data.userName || message.senderUserName || peer.userName;
            peer.userDisplayName = message.data.userDisplayName || message.senderDisplayName || peer.userDisplayName;
            peer.abbreviatedName = message.data.abbreviatedName || message.senderAbbreviatedName || peer.abbreviatedName;

            // Update event counts if available
            if (message.data.attendeeCount !== undefined) { // Corrected access to attendeeCount
                peer.attendeeCount = message.data.attendeeCount; // Corrected access
            }
            if (message.data.eventCount !== undefined) { // Assuming eventCount is also in data
                peer.eventCount = message.data.eventCount;
            }
            if (message.data.isCreator !== undefined) {
                peer.isCreator = message.data.isCreator;
            }
            // NEW: Accept deviceCount from device-info messages for audit purposes
            if (message.data.deviceCount !== undefined) {
                peer.deviceCount = message.data.deviceCount;
                this.debugLoggerService.log('WebRTC Mesh', `Updated peer ${peer.id} deviceCount to ${peer.deviceCount} from device-info message`);
                if (this.currentEventId) {
                    void this.reconcileManualTotal(this.currentEventId);
                }
            }

            this.debugLoggerService.log('WebRTC Mesh', `Updated enhanced device info for peer ${peer.id}:`, {
                deviceName: peer.deviceName,
                platform: peer.platform,
                userDisplayName: peer.userDisplayName,
                abbreviatedName: peer.abbreviatedName,
                attendeeCount: peer.attendeeCount
            });

            // Also update the WebSocket signaling service's peer info
            this.updateSignalingServicePeerInfo(peer);

            this.updateConnectedPeers();

            // NEW: Discrepancy check for QR events
            if (this.currentEventId) {
                const currentEvent = this.eventService.getEventById(this.currentEventId);
                if (currentEvent && currentEvent.countMethod === 'qr' && message.data.attendeeCount !== undefined) {
                    const localTotalCount = await this.attendanceService.getTotalScanned(this.currentEventId);
                    if (message.data.attendeeCount > localTotalCount) {
                        this.debugLoggerService.log('WebRTC Mesh', `Discrepancy detected for event ${this.currentEventId}. Local: ${localTotalCount}, Remote: ${message.data.attendeeCount}. Requesting full sync from ${message.senderId}`);
                        await this.requestFullAttendanceSync(this.currentEventId, message.senderId);
                    } else {
                        this.debugLoggerService.log('WebRTC Mesh', `No discrepancy or remote count not higher for event ${this.currentEventId}. Local: ${localTotalCount}, Remote: ${message.data.attendeeCount}.`);
                    }
                } else if (currentEvent) {
                    this.debugLoggerService.log('WebRTC Mesh', `Discrepancy check skipped: currentEvent is not 'qr' (${currentEvent.countMethod}) or attendeeCount is undefined.`); // Modified log
                } else {
                    this.debugLoggerService.log('WebRTC Mesh', 'Discrepancy check skipped: currentEvent is null.'); // Modified log
                }
            }
        }
    }

    private updateSignalingServicePeerInfo(peer: WebRTCPeer): void {
        // Use the new method to update peer device info with enhanced information
        this.signalingService.updatePeerDeviceInfo(peer.id, {
            deviceName: peer.deviceName,
            platform: peer.platform,
            deviceId: peer.deviceId,
            // Include user identification if available
            userDisplayName: peer.userDisplayName,
            abbreviatedName: peer.abbreviatedName,
            userPhoneNumber: peer.userPhoneNumber,
            attendeeCount: peer.attendeeCount,
            deviceCount: peer.deviceCount, // NEW: Include device count for audit purposes
            isCreator: peer.isCreator || false,
            // Background tracking
            isInBackground: peer.isInBackground,
            lastBackgroundPing: peer.lastBackgroundPing,
            backgroundPingCount: peer.backgroundPingCount
        });
        this.debugLoggerService.log('WebRTC Mesh', `Updated signaling service peer info for ${peer.id}`);

    }

    // Legacy compatibility methods for existing components
    async syncTicketData(): Promise<void> {
        this.debugLoggerService.log('WebRTC Mesh', 'syncTicketData called - this is a legacy method, consider updating to use data channels');
    }

    async checkMeshConnection(): Promise<{
        connected: boolean;
        eventId?: string;
        peerCount: number;
        activePeers: number;
        meshInitialized: boolean;
        diagnostics: any;
    }> {
        const connectedPeers = this.getConnectedPeers();
        this.debugLoggerService.log('WebRTC Mesh', 'Checking mesh connection status');
        return {
            connected: connectedPeers.length > 0,
            peerCount: this.peers.size,
            activePeers: connectedPeers.length,
            meshInitialized: this.isInitialized(),
            diagnostics: {
                totalPeers: this.peers.size,
                connectedPeers: connectedPeers.length,
                signalingConnected: this.signalingService.isConnected,
                clientId: this.signalingService.clientId
            }
        };
    }

    logDiagnostics(context: string = 'general'): void {
        const connectedPeers = this.getConnectedPeers();
        const signalingPeers = this.signalingService.peers;

        this.debugLoggerService.log('WebRTC Diagnostics', `[WebRTC Diagnostics - ${context}]`);
        this.debugLoggerService.log('WebRTC Diagnostics', `- Mesh initialized: ${this.isInitialized()}`);
        this.debugLoggerService.log('WebRTC Diagnostics', `- Signaling connected: ${this.signalingService.isConnected}`);
        this.debugLoggerService.log('WebRTC Diagnostics', `- My Client ID: ${this.signalingService.clientId}`);
        this.debugLoggerService.log('WebRTC Diagnostics', `- Total signaling peers: ${signalingPeers.length}`);
        this.debugLoggerService.log('WebRTC Diagnostics', `- Signaling peers: [${signalingPeers.join(', ')}]`);
        this.debugLoggerService.log('WebRTC Diagnostics', `- Total WebRTC peers: ${this.peers.size}`);
        this.debugLoggerService.log('WebRTC Diagnostics', `- Connected WebRTC peers: ${connectedPeers.length}`);

        this.debugLoggerService.log('WebRTC Diagnostics', `- WebRTC Peer Details:`);
        this.peers.forEach(peer => {
            this.debugLoggerService.log('WebRTC Diagnostics', `  - Peer ${peer.id}: ${peer.connection.connectionState} (${peer.connection.iceConnectionState}) - Device: ${peer.deviceName || 'Unknown'}`);
        });

        this.debugLoggerService.log('WebRTC Diagnostics', `- Connected Devices List:`);
        const devices = this.signalingService.getConnectedDevices();
        devices.forEach((device, id) => {
            this.debugLoggerService.log('WebRTC Diagnostics', `  - Device ${id}: ${device.deviceName || 'Unknown'} (${device.platform || 'Unknown'})`);
        });
    }

    async forceConnections(): Promise<{
        success: boolean;
        connectionAttempts: number;
        successfulConnections: number;
        failedConnections: number;
        details: Record<string, any>;
    }> {
        this.debugLoggerService.log('WebRTC Mesh', 'forceConnections called');

        const availablePeers = this.signalingService.peers;
        const connectionAttempts = availablePeers.length;
        let successfulConnections = 0;
        let failedConnections = 0;
        const details: Record<string, any> = {};

        for (const peerId of availablePeers) {
            if (peerId !== this.signalingService.clientId) {
                try {
                    await this.connectToPeer(peerId);
                    successfulConnections++;
                    details[peerId] = 'success';
                } catch (error) {
                    failedConnections++;
                    details[peerId] = error;
                }
            }
        }

        return {
            success: successfulConnections > 0,
            connectionAttempts,
            successfulConnections,
            failedConnections,
            details
        };
    }

    async enableAudioVideo(options: { audio?: boolean; video?: boolean; mp3FilePath?: string }): Promise<boolean> {
        this.disableAudioVideo(true);

        let stream: MediaStream | undefined;

        if (options.mp3FilePath && options.audio) {
            this.debugLoggerService.log('WebRTC Mesh', `Attempting to broadcast MP3 from: ${options.mp3FilePath}`);
            stream = await this.audioService.startMp3Broadcast(options.mp3FilePath);

            if (!stream) {
                this.debugLoggerService.error('WebRTC Mesh', `Failed to get MP3 stream from ${options.mp3FilePath}`);
                return false;
            }
            this.debugLoggerService.log('WebRTC Mesh', 'MP3 stream acquired for broadcast.');

        } else if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: options.video || false,
                    audio: options.audio || false
                });
                this.debugLoggerService.log('WebRTC Mesh', 'Local media stream (microphone/camera) acquired', {
                    hasVideo: options.video && stream.getVideoTracks().length > 0,
                    hasAudio: options.audio && stream.getAudioTracks().length > 0
                });
            } catch (mediaError) {
                this.debugLoggerService.warn('WebRTC Mesh', `Could not access camera/microphone: ${mediaError}`);
                this.debugLoggerService.log('WebRTC Mesh', 'Continuing without local media stream - data channels will still work');
                return false;
            }
        } else {
            this.debugLoggerService.warn('WebRTC Mesh', 'getUserMedia not available - likely due to HTTP instead of HTTPS');
            this.debugLoggerService.log('WebRTC Mesh', 'Media streaming disabled, but data channels will work');
            return false;
        }

        if (stream) {
            this.localStream = stream;
            await this.addLocalStream(this.localStream);
            this.storeBroadcastIntent(options);
            return true;
        } else {
            this.debugLoggerService.error('WebRTC Mesh', 'No stream available to enable audio/video.');
            return false;
        }
    }

    async disableAudioVideo(keepIntent = false): Promise<void> {
        this.audioService.stopMp3Broadcast();

        if (this.localStream) {
            this.localStream.getTracks().forEach(track => {
                track.stop();
                this.peers.forEach(peer => {
                    const senders = peer.connection.getSenders();
                    senders.forEach(sender => {
                        if (sender.track === track) {
                            peer.connection.removeTrack(sender);
                        }
                    });
                });
            });
            this.localStream = undefined;
            this.debugLoggerService.log('WebRTC Mesh', 'Audio/video streams disabled');
        }

        if (!keepIntent) {
            this.clearBroadcastIntent();
        }
    }

    private storeBroadcastIntent(options: { audio?: boolean; video?: boolean; mp3FilePath?: string }): void {
        try {
            const intent = {
                type: options.mp3FilePath ? 'mp3' : 'mic',
                mp3FilePath: options.mp3FilePath || null,
                audio: options.audio ?? false,
                video: options.video ?? false
            };
            localStorage.setItem(this.BROADCAST_INTENT_KEY, JSON.stringify(intent));
        } catch { /* localStorage may be unavailable */ }
    }

    private clearBroadcastIntent(): void {
        try {
            localStorage.removeItem(this.BROADCAST_INTENT_KEY);
        } catch { /* ignore */ }
    }

    private loadBroadcastIntent(): { type: string; mp3FilePath: string | null; audio: boolean; video: boolean } | null {
        try {
            const raw = localStorage.getItem(this.BROADCAST_INTENT_KEY);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    async restoreBroadcastIfNeeded(): Promise<void> {
        const intent = this.loadBroadcastIntent();
        if (!intent) return;

        this.debugLoggerService.log('WebRTC Mesh', 'Restoring broadcast intent after reconnect', intent);

        const options: { audio?: boolean; video?: boolean; mp3FilePath?: string } = {
            audio: intent.audio,
            video: intent.video
        };
        if (intent.type === 'mp3' && intent.mp3FilePath) {
            options.mp3FilePath = intent.mp3FilePath;
        }

        const success = await this.enableAudioVideo(options);
        if (success) {
            this.debugLoggerService.log('WebRTC Mesh', 'Broadcast restored successfully after reconnect');
            await this.broadcastDataMessage({
                type: 'audio_broadcaster_status',
                isActive: true,
                isMp3: intent.type === 'mp3',
                deviceId: 'local'
            }, 'system');
        } else {
            this.debugLoggerService.warn('WebRTC Mesh', 'Failed to restore broadcast after reconnect');
        }
    }

    isMediaEnabled(): { audio: boolean; video: boolean } {
        if (!this.localStream) {
            return { audio: false, video: false };
        }

        return {
            audio: this.localStream.getAudioTracks().length > 0,
            video: this.localStream.getVideoTracks().length > 0
        };
    }

    /**
     * Store connection history for device recognition
     */
    private async storeConnectionHistory(eventId: string, eventPassword?: string): Promise<void> {
        try {
            const deviceInfo = await this.deviceService.getDeviceInfo();
            const history = await this.getConnectionHistory();

            const newEntry = {
                deviceId: deviceInfo.deviceId,
                deviceName: deviceInfo.deviceName,
                eventId,
                eventPassword: eventPassword || '',
                timestamp: Date.now(),
                userDisplayName: deviceInfo.userDisplayName
            };

            // Remove any existing entry for this device and event
            const filteredHistory = history.filter(entry =>
                !(entry.deviceId === deviceInfo.deviceId && entry.eventId === eventId)
            );

            // Add new entry and keep only the most recent entries
            filteredHistory.unshift(newEntry);
            const limitedHistory = filteredHistory.slice(0, this.MAX_HISTORY_ENTRIES);

            localStorage.setItem(this.CONNECTION_HISTORY_KEY, JSON.stringify(limitedHistory));
            this.debugLoggerService.log('WebRTC Mesh', 'Stored connection history:', newEntry);
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to store connection history: ${error}`);
        }
    }

    /**
     * Get connection history from localStorage
     */
    private async getConnectionHistory(): Promise<any[]> {
        try {
            const historyStr = localStorage.getItem(this.CONNECTION_HISTORY_KEY);
            return historyStr ? JSON.parse(historyStr) : [];
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to get connection history: ${error}`);
            return [];
        }
    }

    /**
     * Find recent connection for this device
     */
    async findRecentConnection(): Promise<{
        eventId: string;
        deviceName: string;
        eventPassword?: string; // Add password to the return type
        timestamp: number;
        userDisplayName?: string;
    } | null> {
        try {
            const deviceInfo = await this.deviceService.getDeviceInfo();
            const history = await this.getConnectionHistory();

            // Find the most recent connection for this device
            const recentConnection = history.find(entry => entry.deviceId === deviceInfo.deviceId);

            if (recentConnection) {
                // Check if the connection is recent (within last 8 hours)
                const hoursSinceConnection = (Date.now() - recentConnection.timestamp) / (1000 * 60 * 60);
                if (hoursSinceConnection < 8) {
                    // NEW: Fetch event from EventService to get the accurate password
                    const storedEvent = this.eventService.getEventById(recentConnection.eventId);
                    if (storedEvent && storedEvent.password) {
                        recentConnection.eventPassword = storedEvent.password;
                    }
                    return recentConnection; // Return the entire entry, including potentially updated password
                }
            }
            return null;
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Failed to find recent connection:', error);
            return null;
        }
    }

    /**
     * Check if there are active peers for a specific event
     */
    async checkActiveEventPeers(eventId: string, eventPassword?: string): Promise<boolean> {
        try {
            // Check if we're currently connected to the signaling server
            if (!this.signalingService.isConnected) {
                // Try to connect to check for active peers
                const connected = await this.signalingService.connect();
                if (!connected) {
                    return false;
                }
            }

            // Always attempt to join the event room to check for active peers
            const joined = await this.signalingService.joinEventRoom(eventId, eventPassword || '');
            if (joined) {
                // Give it a moment to receive peer updates
                await new Promise(resolve => setTimeout(resolve, 1000));
                const hasPeers = this.signalingService.peers.length > 0;
                return hasPeers;
            }

            return false;
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Failed to check active event peers:', error);
            return false;
        }
    }

    /**
     * Check for recent connections and offer reconnection
     */
    async checkAndOfferReconnection(): Promise<{
        shouldReconnect: boolean;
        eventId?: string;
        deviceName?: string;
        userDisplayName?: string;
        eventPassword?: string; // Add password to the return type
    }> {
        try {
            const recentConnection = await this.findRecentConnection();

            if (!recentConnection) {
                return { shouldReconnect: false };
            }

            // Check if there are still active peers for this event
            const hasActivePeers = await this.checkActiveEventPeers(recentConnection.eventId, recentConnection.eventPassword);

            const reconnectionResult = {
                shouldReconnect: hasActivePeers,
                eventId: recentConnection.eventId,
                deviceName: recentConnection.deviceName,
                userDisplayName: recentConnection.userDisplayName,
                eventPassword: recentConnection.eventPassword // Return the password
            };
            return reconnectionResult;
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Failed to check for reconnection:', error);
            return { shouldReconnect: false };
        }
    }

    /**
     * Reconnect to a previous event session
     */
    async reconnectToEvent(eventId: string): Promise<boolean> {
        try {
            const recentConnection = await this.findRecentConnection();
            let eventPassword: string | undefined;

            if (recentConnection && recentConnection.eventId === eventId) {
                eventPassword = recentConnection.eventPassword;
            } else {
                // Fallback: if findRecentConnection didn't return a matching event, try to get from current event
                const currentEvent = this.eventService.getCurrentEvent();
                if (currentEvent && currentEvent.id === eventId) {
                    eventPassword = currentEvent.password;
                } else {
                    // Could not find event password from any source
                }
            }

            // Initialize mesh for the event, passing the password
            await this.initMesh(eventId, false, eventPassword);

            // Broadcast device info to let peers know we're back
            await this.broadcastDeviceInfo();

            // NEW: Request full attendance sync if it's a QR event and we are rejoining
            const currentEvent = this.eventService.getEventById(eventId);

            // CRITICAL DEBUG: Ensure countMethod is a clean string before comparison
            let cleanedCountMethod: string | undefined = currentEvent?.countMethod;
            if (cleanedCountMethod) {
                cleanedCountMethod = String(cleanedCountMethod).trim();
            }
            this.debugLoggerService.log('WebRTC Mesh', `Cleaned countMethod: '${cleanedCountMethod}', Type: ${typeof cleanedCountMethod}`);

            if (currentEvent && cleanedCountMethod === 'qr') {
                this.debugLoggerService.log('WebRTC Mesh', `Rejoined QR event ${eventId}. Requesting full attendance sync.`);
                await this.requestFullAttendanceSync(eventId, this.signalingService.clientId || 'unknown');
            } else if (currentEvent && cleanedCountMethod === 'manual') {
                this.debugLoggerService.log('WebRTC Mesh', `Rejoined manual event ${eventId}. Requesting consensus count sync.`);
                await new Promise(resolve => setTimeout(resolve, 500));
                await this.requestConsensusCountSync(eventId);
            } else {
                this.debugLoggerService.log('WebRTC Mesh', `Rejoined event ${eventId}. Skipping sync request (unknown count method: ${cleanedCountMethod}).`);
            }

            // Restore audio broadcast if this device was broadcasting before disconnect
            await this.restoreBroadcastIfNeeded();

            return true;
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to reconnect to event ${eventId}:`, error);
            return false;
        }
    }

    /**
     * Check event status with the server to determine if reconnection is possible
     */
    async checkEventStatus(eventId: string): Promise<{
        success: boolean;
        status?: {
            eventId: string;
            exists: boolean;
            hasActivePeers: boolean;
            activePeerCount: number;
            wasPreviouslyConnected: boolean;
            isWithin8Hours: boolean;
            shouldReconnect: boolean;
            lastSeenAt?: number;
            eventCreatedAt?: string;
        };
        error?: string;
    }> {
        try {
            const deviceInfo = await this.deviceService.getDeviceInfo();

            this.debugLoggerService.log('WebRTC Mesh', `Checking event status for ${eventId} from device ${deviceInfo.deviceId}`);

            // First try via WebSocket if connected
            if (this.signalingService.isConnected) {
                return await this.checkEventStatusViaWebSocket(eventId, deviceInfo.deviceId);
            } else {
                // Fallback to HTTP API
                return await this.checkEventStatusViaHTTP(eventId, deviceInfo.deviceId);
            }
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to check event status: ${error}`);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Check event status via WebSocket message
     */
    private async checkEventStatusViaWebSocket(eventId: string, deviceId: string): Promise<{
        success: boolean;
        status?: any;
        error?: string;
    }> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                this.debugLoggerService.log('WebRTC Mesh', `Event status check timeout (WebSocket)`);
                if (subscription) {
                    subscription.unsubscribe();
                }
                resolve({
                    success: false,
                    error: 'Timeout waiting for event status response'
                });
            }, 5000);

            // Listen for the response
            const subscription = this.signalingService.messages$.subscribe(message => {
                if (message.type === 'event-status-response' && message.eventId === eventId) {
                    clearTimeout(timeout);
                    subscription.unsubscribe();

                    this.debugLoggerService.log('WebRTC Mesh', `Received event status response:`, message.status);
                    resolve({
                        success: true,
                        status: message.status
                    });
                }
            });

            // Send the request
            try {
                this.signalingService.sendMessage({
                    type: 'check-event-status',
                    eventId,
                    deviceId
                } as any);
            } catch (error) {
                clearTimeout(timeout);
                subscription.unsubscribe();
                resolve({
                    success: false,
                    error: error instanceof Error ? error.message : 'Failed to send message'
                });
            }
        });
    }

    /**
     * Check event status via HTTP API
     */
    private async checkEventStatusViaHTTP(eventId: string, deviceId: string): Promise<{
        success: boolean;
        status?: any;
        error?: string;
    }> {
        try {
            // Use the signaling server URL but change to HTTP/HTTPS
            const wsUrl = this.config.signalingServerUrl;
            const httpUrl = wsUrl.replace('wss://', 'https://').replace('ws://', 'http://');
            const apiUrl = `${httpUrl}/api/events/check-status`;

            this.debugLoggerService.log('WebRTC Mesh', `Checking event status via HTTP: ${apiUrl}`);

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ eventId, deviceId })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                this.debugLoggerService.log('WebRTC Mesh', `Received event status via HTTP:`, data.status);
                return {
                    success: true,
                    status: data.status
                };
            } else {
                return {
                    success: false,
                    error: data.message || 'Failed to get event status'
                };
            }
        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `HTTP event status check failed: ${error}`);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Network error'
            };
        }
    }

    private async sendDeviceIdentification(): Promise<void> {
        try {
            await this.signalingService.sendDeviceIdentification();
            this.debugLoggerService.log('WebRTC Mesh', `Sent device identification to signaling server`);

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', `Failed to send device identification: ${error}`);
        }
    }

    // Public getter for currentEventId
    public getCurrentEventId(): string | null {
        return this.currentEventId;
    }

    /**
     * Check if the current device is the event creator
     */
    public isEventCreator(): boolean {
        return this._isCreator;
    }

    /**
     * Set background/foreground state and sync when coming to foreground
     */
    public setBackgroundState(isBackground: boolean): void {
        this.isInBackground = isBackground;
        if (!isBackground) {
            // App came to foreground - request full sync
            this.syncOnForegroundResume();
        }
    }

    /**
     * Request count sync when app resumes from background
     * Enhanced with consensus mechanism for manual events
     */
    private async syncOnForegroundResume(): Promise<void> {
        const currentEvent = this.currentEventId;
        if (!currentEvent) return;

        const event = this.eventService.getEventById(currentEvent);
        if (!event) return;

        this.debugLoggerService.log('WebRTC Mesh', 'App resumed to foreground - requesting count sync');

        if (event.countMethod === 'manual') {
            // For manual events, use consensus-based sync to resolve conflicts
            await this.requestConsensusCountSync(currentEvent);
        } else if (event.countMethod === 'qr') {
            // For QR events, request full ticket sync
            await this.broadcastDataMessage({
                type: 'full-attendance-sync-request',
                eventId: currentEvent,
                requesterId: this.signalingService.clientId
            }, 'system');
        } else {
            // Fallback to standard count sync
            await this.broadcastDataMessage({
                type: 'request-count-sync',
                eventId: currentEvent,
                timestamp: Date.now()
            }, 'system');
        }

        this.lastForegroundSync = Date.now();
    }

    /**
     * Check if a device was previously connected (indicates reconnection)
     */
    private wasDevicePreviouslyConnected(peerId: string): boolean {
        // Check if this peer ID exists in our last seen map
        // If it does, this is a reconnection, not a first-time connection
        return this.lastSeenMap.has(peerId);
    }

    /**
     * Request count from all peers and use conservative consensus
     * For manual counting, avoid reducing totals to prevent losing offline counts
     */
    private async requestConsensusCountSync(eventId: string): Promise<void> {
        const SYNC_TIMEOUT = 3000; // 3 seconds
        const requestId = `consensus-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const responseMap = new Map<string, { totalCount: number; deviceCount: number }>();

        this.debugLoggerService.log('WebRTC Mesh', '🔄 Starting consensus count sync (manual, non-decreasing)', { eventId, requestId });

        // Store the request handler for this specific request
        const consensusHandler = (message: any) => {
            if (message.type === 'count-consensus-response' && 
                message.requestId === requestId && 
                message.eventId === eventId) {
                
                const totalCount = message.totalCount ?? message.data?.totalCount ?? 0;
                const deviceCount = message.deviceCount ?? message.data?.deviceCount ?? 0;
                const senderId = message.senderId || message.data?.senderId;
                
                if (senderId) {
                    responseMap.set(senderId, { totalCount, deviceCount });
                    this.debugLoggerService.log('WebRTC Mesh', `📊 Received consensus response from ${senderId}: total=${totalCount}, device=${deviceCount}`);
                }
            }
        };

        // Store handler for cleanup
        (this as any)[`_consensusHandler_${requestId}`] = consensusHandler;

        // Create promise to collect responses
        const responsePromise = new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
                this.debugLoggerService.log('WebRTC Mesh', `⏱️ Consensus sync timeout - collected ${responseMap.size} responses`);
                resolve();
            }, SYNC_TIMEOUT);

            // Check for responses periodically
            const checkInterval = setInterval(() => {
                // If we got responses from all peers, resolve early
                if (responseMap.size >= this.peers.size && this.peers.size > 0) {
                    clearTimeout(timeout);
                    clearInterval(checkInterval);
                    this.debugLoggerService.log('WebRTC Mesh', `✅ Got responses from all ${responseMap.size} peers - proceeding early`);
                    resolve();
                }
            }, 100);

            // Cleanup interval on timeout
            setTimeout(() => clearInterval(checkInterval), SYNC_TIMEOUT);
        });

        // Broadcast consensus request
        await this.broadcastDataMessage({
            type: 'request-count-consensus',
            eventId: eventId,
            requesterId: this.signalingService.clientId,
            timestamp: Date.now(),
            requestId: requestId
        }, 'system');

        // Wait for responses
        await responsePromise;

        // Cleanup handler
        delete (this as any)[`_consensusHandler_${requestId}`];

        // Analyze responses - use HIGHEST count
        try {
            const localTotalCount = await this.attendanceService.getCount(eventId);
            const localDeviceCount = await this.attendanceService.getDeviceCount(eventId);
            const totalCounts: number[] = [localTotalCount];
            const deviceCounts: number[] = [localDeviceCount];
            
            // Add peer responses
            responseMap.forEach((counts, peerId) => {
                totalCounts.push(counts.totalCount);
                deviceCounts.push(counts.deviceCount);
                this.debugLoggerService.log('WebRTC Mesh', `📊 Peer ${peerId} reported total=${counts.totalCount}, device=${counts.deviceCount}`);
            });

            this.debugLoggerService.log('WebRTC Mesh', '📊 Consensus analysis:', {
                method: 'Sum Device Counts (manual events)',
                totalResponses: totalCounts.length,
                totalCounts,
                deviceCounts,
                localTotalCount,
                localDeviceCount,
                peerResponses: responseMap.size
            });

            if (totalCounts.length === 1) {
                this.debugLoggerService.warn('WebRTC Mesh', '⚠️ No peer responses received - keeping local count');
                return;
            }

            const summedDeviceCounts = deviceCounts.reduce((sum, c) => sum + (c ?? 0), 0);
            // Device counts may be negative (exit phones). Floor only the general total.
            const targetCount = Math.max(0, summedDeviceCounts);
            const highestTotal = Math.max(...totalCounts);
            const averageTotal = Math.round(totalCounts.reduce((sum, c) => sum + c, 0) / totalCounts.length);

            this.debugLoggerService.log('WebRTC Mesh', `📊 SumDevices: ${summedDeviceCounts}, FlooredTotal: ${targetCount}, Highest: ${highestTotal}, Average: ${averageTotal}, LocalTotal: ${localTotalCount}`);

            if (targetCount !== localTotalCount) {
                this.debugLoggerService.log('WebRTC Mesh',
                    `⚠️ Count mismatch detected! Local: ${localTotalCount}, Consensus: ${targetCount}, Average: ${averageTotal}`);

                await this.attendanceService.switchEvent(eventId, 'manual');
                await this.storage.set(`count_${eventId}`, targetCount.toString());

                const activeEventId = this.attendanceService.currentEventId();
                if (activeEventId === eventId || !activeEventId) {
                    (this.attendanceService as any).countSignal?.set(targetCount);
                }

                await this.broadcastCurrentCountState(eventId);
                await this.showSyncNotification(localTotalCount, targetCount, totalCounts.length);

                this.debugLoggerService.log('WebRTC Mesh', `✅ Count synchronized to consensus value: ${targetCount}`);
            } else {
                this.debugLoggerService.log('WebRTC Mesh', '✅ Count already in sync - no adjustment needed');
            }

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Error processing consensus sync:', error);
        }
    }

    /**
     * Show toast notification when count is synchronized
     */
    private async showSyncNotification(oldCount: number, newCount: number, peerCount: number): Promise<void> {
        try {
            const message = await this.translateService.get('SYNC.COUNT_CORRECTED', {
                oldCount,
                newCount,
                peerCount
            }).toPromise();

            const toast = await this.toastController.create({
                message: message || `Count synchronized: ${oldCount} → ${newCount} (from ${peerCount} devices)`,
                duration: 4000,
                position: 'top',
                color: 'warning',
                icon: 'sync-outline',
                buttons: [
                    {
                        text: 'OK',
                        role: 'cancel'
                    }
                ]
            });

            await toast.present();
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Could not show sync notification:', error);
        }
    }

    private markDecreaseAllowed(eventId: string, reason: string): void {
        const until = Date.now() + this.DECREASE_ALLOW_WINDOW_MS;
        this.allowDecreaseUntilByEvent.set(eventId, until);
        this.debugLoggerService.log('WebRTC Mesh', '✅ Allowing manual count decreases temporarily', {
            eventId,
            reason,
            until
        });
    }

    private isDecreaseAllowed(eventId: string): boolean {
        const until = this.allowDecreaseUntilByEvent.get(eventId);
        if (!until) return false;
        if (Date.now() > until) {
            this.allowDecreaseUntilByEvent.delete(eventId);
            return false;
        }
        return true;
    }

    private async showAdminDisconnectNotification(): Promise<void> {
        try {
            const toast = await this.toastController.create({
                message: this.translateService.instant('MESH.DISCONNECTED_BY_ADMIN'),
                duration: 4000,
                position: 'top',
                color: 'warning',
                buttons: [
                    {
                        text: this.translateService.instant('COMMON.OK'),
                        role: 'cancel'
                    }
                ]
            });
            await toast.present();
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Could not show admin disconnect notification:', error);
        }
    }

    private async showReconnectBlockedNotification(blocked: boolean): Promise<void> {
        try {
            const toast = await this.toastController.create({
                message: blocked
                    ? 'Auto-reconnect blocked by admin for this event.'
                    : 'Auto-reconnect allowed by admin for this event.',
                duration: 4000,
                position: 'top',
                color: blocked ? 'warning' : 'success'
            });
            await toast.present();
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Could not show reconnect status notification:', error);
        }
    }

    private async showAdminRemoveEventNotification(): Promise<void> {
        try {
            const toast = await this.toastController.create({
                message: 'This event was removed by an admin. Rejoin is required.',
                duration: 4000,
                position: 'top',
                color: 'warning'
            });
            await toast.present();
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Could not show admin remove event notification:', error);
        }
    }

    private async showApprovedAdoptionNotification(eventName: string): Promise<void> {
        try {
            const toast = await this.toastController.create({
                message: `Access approved. You were adopted into ${eventName}.`,
                duration: 4500,
                position: 'top',
                color: 'success'
            });
            await toast.present();
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Could not show approved adoption notification:', error);
        }
    }

    private async showOwnerConflictAlert(params: {
        ownerEventId: string;
        ownerEventName: string;
        requesterLabel: string;
    }): Promise<void> {
        try {
            const existingAlert = await this.alertController.getTop();
            if (existingAlert) {
                await existingAlert.dismiss().catch(() => undefined);
            }

            const alert = await this.alertController.create({
                header: 'Duplicate QR Event Request',
                subHeader: params.ownerEventName,
                message: `${params.requesterLabel} scanned the same QR event on another device. Open the admin panel to approve or reject the access request and resolve the conflict.`,
                backdropDismiss: false,
                buttons: [
                    {
                        text: 'Later',
                        role: 'cancel'
                    },
                    {
                        text: 'Open Admin',
                        handler: () => {
                            void this.openOwnerAdminPanel(params.ownerEventId);
                        }
                    }
                ]
            });

            await alert.present();
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Could not show owner conflict alert:', error);
        }
    }

    private resolveEventAdminPassword(eventId: string): string | null {
        const event = this.eventService.getEventById(eventId);
        if (event?.password) {
            return event.password;
        }

        try {
            const stored = localStorage.getItem(`event_${eventId}_details`);
            if (!stored) {
                return null;
            }
            const parsed = JSON.parse(stored);
            const password = typeof parsed?.password === 'string' ? parsed.password : '';
            return password.trim() ? password : null;
        } catch {
            return null;
        }
    }

    private async openOwnerAdminPanel(eventId: string): Promise<void> {
        const event = this.eventService.getEventById(eventId);
        const password = this.resolveEventAdminPassword(eventId);

        if (!event || !password) {
            await this.showOwnerConflictAdminOpenFailure('Owner event password is missing. Share the event again before opening admin.');
            return;
        }

        if (!event.password) {
            this.eventService.updateEventPassword(event.id, password);
        }

        let tokenResult = await this.serverSyncService.getEventAdminTokenResult(event.id, password);
        if (!tokenResult.token && tokenResult.status === 401) {
            const reset = await this.serverSyncService.setEventPassword(event.id, password);
            if (reset) {
                tokenResult = await this.serverSyncService.getEventAdminTokenResult(event.id, password);
            }
        }

        if (!tokenResult.token) {
            await this.showOwnerConflictAdminOpenFailure(tokenResult.message || 'Unable to open event admin.');
            return;
        }

        const adminDeviceId = await this.serverSyncService.getCurrentDeviceId();
        const url = `${this.serverSyncService.getServerUrl()}/admin.html?eventId=${encodeURIComponent(event.id)}&token=${encodeURIComponent(tokenResult.token)}&deviceId=${encodeURIComponent(adminDeviceId)}`;
        if (Capacitor.isNativePlatform()) {
            try {
                await Browser.open({ url });
                return;
            } catch (error) {
                this.debugLoggerService.warn('WebRTC Mesh', 'Failed to open admin panel via Capacitor Browser, falling back to window.open', error);
            }
        }
        window.open(url, '_blank');
    }

    private async showOwnerConflictAdminOpenFailure(message: string): Promise<void> {
        try {
            const toast = await this.toastController.create({
                message,
                duration: 3500,
                position: 'top',
                color: 'warning'
            });
            await toast.present();
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', 'Could not show admin-open failure notification:', error);
        }
    }

    private async getBlockedReconnectMap(): Promise<Record<string, boolean>> {
        try {
            const raw = await this.storage.get(this.BLOCKED_RECONNECT_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    }

    private async setReconnectBlocked(eventId: string, blocked: boolean): Promise<void> {
        const map = await this.getBlockedReconnectMap();
        if (blocked) {
            map[eventId] = true;
        } else {
            delete map[eventId];
        }
        await this.storage.set(this.BLOCKED_RECONNECT_KEY, JSON.stringify(map));
    }

    private async isReconnectBlocked(eventId: string): Promise<boolean> {
        const map = await this.getBlockedReconnectMap();
        return !!map[eventId];
    }

    /**
     * Attempt to reconnect to the WebRTC mesh with exponential backoff
     */
    public async attemptReconnection(eventId: string): Promise<void> {
        // Don't start a new reconnection if one is already in progress
        if (this._reconnectionStatusSignal() === 'reconnecting') {
            return;
        }

        // Reset if this is a new reconnection attempt after being idle
        if (this._reconnectionStatusSignal() === 'idle') {
            this.reconnectionAttempts = 0;
        }

        // Check if we've exceeded max attempts
        if (this.reconnectionAttempts >= this.MAX_RECONNECTION_ATTEMPTS) {
            this._reconnectionStatusSignal.set('failed');
            this.debugLoggerService.error('WebRTC Mesh', 'Max reconnection attempts reached');
            return;
        }

        this.reconnectionAttempts++;
        this._reconnectionStatusSignal.set('reconnecting');

        this.debugLoggerService.log('WebRTC Mesh', `Reconnection attempt ${this.reconnectionAttempts}/${this.MAX_RECONNECTION_ATTEMPTS}`);

        try {
            // Check if event is still active on server
            const eventStatus = await this.checkActiveEventPeers(eventId);

            if (!eventStatus) {
                this.debugLoggerService.log('WebRTC Mesh', 'Event no longer active on server');
                this._reconnectionStatusSignal.set('failed');
                return;
            }

            // Attempt to rejoin the event room
            await this.joinEventRoom(eventId);

            // Wait a bit for connections to establish
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Check if we successfully reconnected
            const connectedPeers = this.getConnectedPeers();
            if (connectedPeers.length > 0) {
                this.debugLoggerService.log('WebRTC Mesh', 'Reconnection successful');
                this._reconnectionStatusSignal.set('idle');
                this.reconnectionAttempts = 0;
                return;
            }

            // Schedule retry with exponential backoff
            const delay = Math.min(5000 * Math.pow(2, this.reconnectionAttempts - 1), 20000);
            this.debugLoggerService.log('WebRTC Mesh', `Retrying reconnection in ${delay}ms`);

            if (this.reconnectionTimer) {
                clearTimeout(this.reconnectionTimer);
            }

            this.reconnectionTimer = setTimeout(() => {
                this.attemptReconnection(eventId);
            }, delay);

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Reconnection attempt failed:', error);

            // Schedule retry with exponential backoff
            const delay = Math.min(5000 * Math.pow(2, this.reconnectionAttempts - 1), 20000);
            this.debugLoggerService.log('WebRTC Mesh', `Retrying reconnection in ${delay}ms`);

            if (this.reconnectionTimer) {
                clearTimeout(this.reconnectionTimer);
            }

            this.reconnectionTimer = setTimeout(() => {
                this.attemptReconnection(eventId);
            }, delay);
        }
    }

    // NEW: Ticket sharing method
    public async shareTicketsWithPeer(peerId: string, tickets: any[], eventId: string): Promise<void> {
        this.sendDataMessage({
            type: 'shared_tickets',
            data: { tickets, eventId },
            timestamp: Date.now(),
            senderId: (await this.deviceService.getDeviceInfo()).deviceId,
            senderDeviceName: (await this.deviceService.getDeviceInfo()).userDisplayName,
            eventId: eventId
        }, peerId);
        this.debugLoggerService.log('WebRTC Mesh', `Shared ${tickets.length} tickets with peer ${peerId} for event ${eventId}`);
    }

    /**
     * Request a full attendance sync from a specific peer (usually the creator).
     */
    public async requestFullAttendanceSync(eventId: string, targetPeerId: string): Promise<void> {
        this.debugLoggerService.log('WebRTC Mesh', `Requesting full attendance sync for event ${eventId} from peer ${targetPeerId}`);
        await this.sendDataMessage({
            type: 'system',
            data: {
                type: 'request-full-attendance-sync',
                eventId,
                requesterId: this.signalingService.clientId
            },
            timestamp: Date.now(),
            senderId: this.signalingService.clientId || 'unknown'
        }, targetPeerId);
    }

    /**
     * Handle reconnection to an event - sync with current mesh state
     */
    async handleEventReconnection(eventId: string): Promise<void> {
        this.debugLoggerService.log('WebRTC Mesh', `Handling reconnection to event: ${eventId}`);

        try {
            // Wait a moment for peer connections to stabilize
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Get connected peers
            const connectedPeers = this.getConnectedPeers();
            if (connectedPeers.length === 0) {
                this.debugLoggerService.log('WebRTC Mesh', 'No peers connected for sync on reconnection');
                return;
            }

            // Get local count
            const localCount = await this.attendanceService.getCount(eventId);

            // Request current count information from all peers
            const syncRequests = connectedPeers.map(peer =>
                this.requestCurrentCountFromPeer(peer.id)
            );

            // Wait for responses (with timeout)
            const responses = await Promise.allSettled(
                syncRequests.map(request =>
                    Promise.race([
                        request,
                        new Promise((_, reject) => setTimeout(() => reject(new Error('Sync timeout')), 5000))
                    ])
                )
            );

            // Analyze responses
            const validResponses = responses
                .filter(result => result.status === 'fulfilled')
                .map(result => (result as PromiseFulfilledResult<any>).value)
                .filter(response => response && typeof response.totalCount === 'number');

            if (validResponses.length === 0) {
                this.debugLoggerService.log('WebRTC Mesh', 'No valid sync responses received');
                return;
            }

            // Calculate consensus from peer responses
            const peerTotalCounts = validResponses.map(r => r.totalCount);
            const uniqueCounts = [...new Set(peerTotalCounts)];
            const peerConsensus = this.calculateConsensusCount(peerTotalCounts);

            this.debugLoggerService.log('WebRTC Mesh', `Reconnection sync analysis:`, {
                localCount,
                peerCounts: peerTotalCounts,
                uniqueCounts,
                peerConsensus
            });

            // Determine sync strategy based on discrepancy
            const discrepancy = Math.abs(localCount - peerConsensus);
            const SMALL_DISCREPANCY_THRESHOLD = 3; // Auto-sync for differences ≤ 3
            const LARGE_DISCREPANCY_THRESHOLD = 10; // Always request user intervention for differences > 10

            if (discrepancy === 0) {
                this.debugLoggerService.log('WebRTC Mesh', 'Perfect sync: local count matches peer consensus');
                return;
            }

            if (discrepancy <= SMALL_DISCREPANCY_THRESHOLD) {
                // Small discrepancy: auto-sync to higher count
                const targetCount = Math.max(localCount, peerConsensus);
                await this.syncToConsensusCount(eventId, targetCount,
                    `Auto-sync on reconnection: small discrepancy (±${discrepancy}), choosing higher count`);
                return;
            }

            // Large discrepancy: trigger audit for user decision
            if (discrepancy > LARGE_DISCREPANCY_THRESHOLD || uniqueCounts.length > 2) {
                this.debugLoggerService.log('WebRTC Mesh',
                    `Large discrepancy detected on reconnection (${discrepancy}). Triggering audit.`);

                // Delay to allow UI to settle, then trigger audit
                setTimeout(() => {
                    this.triggerReconnectionAudit(eventId, localCount, peerConsensus, {
                        localCount,
                        peerCounts: peerTotalCounts,
                        discrepancy,
                        reason: 'Device reconnection with significant count difference'
                    });
                }, 1000);
                return;
            }

            // Medium discrepancy: request full data sync but don't auto-update
            this.debugLoggerService.log('WebRTC Mesh',
                `Medium discrepancy (${discrepancy}). Requesting full sync for comparison.`);

            await this.requestFullDataSyncForComparison(eventId, localCount, peerConsensus);

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Error during reconnection sync:', error);
        }
    }

    /**
     * Calculate consensus count from peer responses
     */
    private calculateConsensusCount(counts: number[]): number {
        if (counts.length === 0) return 0;
        if (counts.length === 1) return counts[0];

        // Remove outliers (more than 2 standard deviations from mean)
        const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
        const variance = counts.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / counts.length;
        const stdDev = Math.sqrt(variance);

        const filtered = counts.filter(count => Math.abs(count - mean) <= 2 * stdDev);

        if (filtered.length === 0) return Math.round(mean); // Fallback if all are outliers

        // Use median of filtered values
        const sorted = filtered.sort((a, b) => a - b);
        return sorted[Math.floor(sorted.length / 2)];
    }

    /**
 * Trigger audit modal for reconnection discrepancies
 */
    private async triggerReconnectionAudit(eventId: string, localCount: number, peerConsensus: number, details: any): Promise<void> {
        try {
            this.debugLoggerService.log('WebRTC Mesh',
                `🔍 RECONNECTION AUDIT TRIGGERED: Local=${localCount}, Peer Consensus=${peerConsensus}, Discrepancy=${details.discrepancy}`);

            // Emit event for components to listen to
            this._reconnectionAuditRequested.next({
                eventId,
                localCount,
                peerConsensus,
                details: {
                    ...details,
                    timestamp: Date.now(),
                    triggerReason: 'Device reconnection with significant count difference'
                }
            });

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Error triggering reconnection audit:', error);
        }
    }

    /**
     * Request full data sync for comparison without auto-updating
     */
    private async requestFullDataSyncForComparison(eventId: string, localCount: number, peerConsensus: number): Promise<void> {
        try {
            this.debugLoggerService.log('WebRTC Mesh',
                `Requesting full data sync for comparison. Local: ${localCount}, Peer: ${peerConsensus}`);

            // Request full attendance data from peers
            await this.requestFullAttendanceSync(eventId, 'comparison-only');

            // The actual comparison and decision logic would happen when the data is received
            // This preserves both local data and peer data for user review

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Error requesting full data sync for comparison:', error);
        }
    }

    /**
     * Request current count from a specific peer
     */
    private async requestCurrentCountFromPeer(peerId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const peer = this.peers.get(peerId);
            if (!peer || !peer.dataChannel || peer.dataChannel.readyState !== 'open') {
                reject(new Error('Peer not available'));
                return;
            }

            const requestId = Date.now().toString();
            const timeout = setTimeout(() => {
                this.syncRequestHandlers.delete(requestId);
                reject(new Error('Request timeout'));
            }, 3000);

            // Store response handler
            this.syncRequestHandlers.set(requestId, (response: any) => {
                clearTimeout(timeout);
                this.syncRequestHandlers.delete(requestId);
                resolve(response);
            });

            // Send sync request
            const message = {
                type: 'sync-request',
                requestId,
                timestamp: Date.now()
            };

            try {
                peer.dataChannel.send(JSON.stringify(message));
            } catch (error) {
                clearTimeout(timeout);
                this.syncRequestHandlers.delete(requestId);
                reject(error);
            }
        });
    }

    /**
     * Sync local count to consensus count
     */
    private async syncToConsensusCount(eventId: string, consensusCount: number, reason: string): Promise<void> {
        try {
            // Get current local count
            const currentCount = await this.attendanceService.getCount(eventId);

            if (currentCount === consensusCount) {
                this.debugLoggerService.log('WebRTC Mesh', `Already synced: local count (${currentCount}) matches consensus`);
                return;
            }

            this.debugLoggerService.log('WebRTC Mesh', `Syncing count: ${currentCount} → ${consensusCount}. Reason: ${reason}`);

            // Update local count to match consensus
            await this.attendanceService.processRemoteTotalCountUpdate(eventId, consensusCount);

            // Broadcast updated device info to peers
            await this.broadcastDeviceInfo();

            this.debugLoggerService.log('WebRTC Mesh', 'Successfully synced to consensus count');

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Error syncing to consensus count:', error);
        }
    }

    /**
     * Handle sync request from a peer - respond with current count information
     */
    private async handleSyncRequest(message: any): Promise<void> {
        try {
            const currentEvent = this.eventService.getCurrentEvent();
            if (!currentEvent) {
                this.debugLoggerService.warn('WebRTC Mesh', 'No current event for sync request');
                return;
            }

            const currentCount = await this.attendanceService.getCount(currentEvent.id);
            const deviceInfo = await this.deviceService.getDeviceInfo();

            const syncResponse = {
                type: 'sync-response',
                requestId: message.requestId,
                eventId: currentEvent.id,
                totalCount: currentCount,
                deviceInfo,
                timestamp: Date.now()
            };

            // Send response to requesting peer
            const peer = Array.from(this.peers.values()).find(p => p.deviceId === message.deviceId);
            if (peer && peer.dataChannel && peer.dataChannel.readyState === 'open') {
                peer.dataChannel.send(JSON.stringify(syncResponse));
                this.debugLoggerService.log('WebRTC Mesh', `Sent sync response to ${message.deviceId}:`, syncResponse);
            } else {
                this.debugLoggerService.warn('WebRTC Mesh', `Could not send sync response - peer ${message.deviceId} not found or channel not open`);
            }

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Error handling sync request:', error);
        }
    }

    /**
     * Handle count consensus request from a peer
     * Responds with current total count
     */
    private async handleCountConsensusRequest(message: any): Promise<void> {
        try {
            const currentEvent = this.eventService.getCurrentEvent();
            if (!currentEvent || currentEvent.id !== message.eventId) {
                this.debugLoggerService.warn('WebRTC Mesh', 'No matching event for consensus request', {
                    currentEventId: currentEvent?.id,
                    requestedEventId: message.eventId
                });
                return;
            }

            const totalCount = await this.attendanceService.getCount(currentEvent.id);
            const deviceCount = await this.attendanceService.getDeviceCount(currentEvent.id);

            this.debugLoggerService.log('WebRTC Mesh', 
                `📤 Sending consensus response - Total: ${totalCount}, RequestID: ${message.requestId}`);

            // Send specific consensus response with total count
            await this.broadcastDataMessage({
                type: 'count-consensus-response',
                eventId: message.eventId,
                requestId: message.requestId,
                requesterId: message.requesterId,
                totalCount: totalCount,
                deviceCount: deviceCount,
                senderId: this.signalingService.clientId,
                timestamp: Date.now()
            }, 'system');

        } catch (error) {
            this.debugLoggerService.error('WebRTC Mesh', 'Error handling count consensus request:', error);
        }
    }

    /**
     * Handle sync response from a peer
     */
    private handleSyncResponse(message: any): void {
        const handler = this.syncRequestHandlers.get(message.requestId);
        if (handler) {
            handler(message);
            this.debugLoggerService.log('WebRTC Mesh', `Processed sync response for request ${message.requestId}`);
        } else {
            this.debugLoggerService.warn('WebRTC Mesh', `No handler found for sync response ${message.requestId}`);
        }
    }

    /**
     * Get last known device counts from signaling service and cached peers
     */
    public getLastKnownDeviceCounts(eventId: string): Array<{
        deviceId: string;
        deviceName: string;
        platform?: string;
        userDisplayName?: string;
        deviceCount: number;
        totalCount: number;
        lastSeen: number;
        connectionStatus: 'disconnected' | 'cached';
        source: 'signaling' | 'webrtc_cache' | 'connection_history';
    }> {
        const lastKnownDevices: Array<any> = [];
        const processedDeviceIds = new Set<string>();

        console.log('🔍 LAST KNOWN COUNTS: Gathering last known device counts for audit');
        this.debugLoggerService.log('WebRTC Mesh', `Getting last known device counts for event: ${eventId}`);

        // 1. Check WebSocket signaling service for known devices
        const signalingDevices = this.signalingService.connectedDevicesSignal();
        console.log('🔍 LAST KNOWN COUNTS: Signaling service devices:', signalingDevices.size);

        for (const [clientId, peerInfo] of signalingDevices.entries()) {
            if (peerInfo.deviceId && !processedDeviceIds.has(peerInfo.deviceId)) {
                if (peerInfo.deviceCount !== undefined && peerInfo.attendeeCount !== undefined) {
                    lastKnownDevices.push({
                        deviceId: peerInfo.deviceId,
                        deviceName: peerInfo.deviceName || 'Unknown Device',
                        platform: peerInfo.platform,
                        userDisplayName: peerInfo.userDisplayName,
                        deviceCount: peerInfo.deviceCount,
                        totalCount: peerInfo.attendeeCount,
                        lastSeen: peerInfo.lastSeen || 0,
                        connectionStatus: 'disconnected',
                        source: 'signaling'
                    });

                    processedDeviceIds.add(peerInfo.deviceId);
                    console.log('🔍 LAST KNOWN COUNTS: Added from signaling:', peerInfo.deviceName, 'Device:', peerInfo.deviceCount, 'Total:', peerInfo.attendeeCount);
                }
            }
        }

        // 2. Check cached WebRTC peers that are not currently connected
        const allPeers = Array.from(this.peers.values());
        const disconnectedPeers = allPeers.filter(peer =>
            !peer.isConnected &&
            peer.deviceId &&
            !processedDeviceIds.has(peer.deviceId)
        );

        console.log('🔍 LAST KNOWN COUNTS: Disconnected WebRTC peers:', disconnectedPeers.length);

        for (const peer of disconnectedPeers) {
            if (peer.deviceCount !== undefined && peer.attendeeCount !== undefined) {
                lastKnownDevices.push({
                    deviceId: peer.deviceId!,
                    deviceName: peer.deviceName || 'Unknown Device',
                    platform: peer.platform,
                    userDisplayName: peer.userDisplayName,
                    deviceCount: peer.deviceCount,
                    totalCount: peer.attendeeCount,
                    lastSeen: peer.lastSeen || 0,
                    connectionStatus: 'disconnected',
                    source: 'webrtc_cache'
                });

                processedDeviceIds.add(peer.deviceId!);
                console.log('🔍 LAST KNOWN COUNTS: Added from WebRTC cache:', peer.deviceName, 'Device:', peer.deviceCount, 'Total:', peer.attendeeCount);
            }
        }

        // 3. Check local storage connection history for additional devices
        try {
            const connectionHistory = localStorage.getItem(this.CONNECTION_HISTORY_KEY);
            if (connectionHistory) {
                const history = JSON.parse(connectionHistory);
                console.log('🔍 LAST KNOWN COUNTS: Connection history entries:', history.length);

                for (const entry of history) {
                    if (entry.deviceId &&
                        !processedDeviceIds.has(entry.deviceId) &&
                        entry.deviceCount !== undefined &&
                        entry.attendeeCount !== undefined) {

                        // Only include recent entries (within last 24 hours)
                        const age = Date.now() - (entry.lastSeen || 0);
                        if (age < 24 * 60 * 60 * 1000) {
                            lastKnownDevices.push({
                                deviceId: entry.deviceId,
                                deviceName: entry.deviceName || 'Unknown Device',
                                platform: entry.platform,
                                userDisplayName: entry.userDisplayName,
                                deviceCount: entry.deviceCount,
                                totalCount: entry.attendeeCount,
                                lastSeen: entry.lastSeen || 0,
                                connectionStatus: 'cached',
                                source: 'connection_history'
                            });

                            processedDeviceIds.add(entry.deviceId);
                            console.log('🔍 LAST KNOWN COUNTS: Added from history:', entry.deviceName, 'Device:', entry.deviceCount, 'Total:', entry.attendeeCount);
                        }
                    }
                }
            }
        } catch (error) {
            this.debugLoggerService.warn('WebRTC Mesh', `Failed to read connection history for last known counts: ${error}`);
        }

        // Sort by last seen (most recent first)
        lastKnownDevices.sort((a, b) => b.lastSeen - a.lastSeen);

        console.log('🔍 LAST KNOWN COUNTS: Found', lastKnownDevices.length, 'devices with last known counts');
        this.debugLoggerService.log('WebRTC Mesh', `Found ${lastKnownDevices.length} devices with last known counts:`, lastKnownDevices);

        return lastKnownDevices;
    }
}